/*
* Palo Alto Theme
*
* Use this file to add custom Javascript to Palo Alto. Keeping your custom
* Javascript in this file will make it easier to update Palo Alto. In order
* to use this file you will need to open layout/theme.liquid and uncomment
* the custom.js script import line near the bottom of the file.
*/

class ProductAccordions {
  constructor() {
    // Cache DOM elements to avoid repeated lookups
    this.productAccordions = null;
    this.faqButtons = null;
    this.faqContents = null;

    // Bind methods to preserve 'this' context
    this.initializeAll = this.initializeAll.bind(this);
    this.initProductAccordions = this.initProductAccordions.bind(this);
    this.initFaqs = this.initFaqs.bind(this);
    this.handleAccordionClick = this.handleAccordionClick.bind(this);
    this.handleFaqClick = this.handleFaqClick.bind(this);
    this.handleResize = this.handleResize.bind(this);

    // Initialize event listeners
    document.addEventListener('DOMContentLoaded', () => {
      // Initialize with a single short timeout to ensure theme is ready
      setTimeout(this.initializeAll, 300);
    });
  }

  // Initialize all components
  initializeAll() {
    // Only run on product pages
    if (!document.body.classList.contains('template-product')) return;

    // Initialize product accordions
    this.initProductAccordions();

    // Initialize FAQs
    this.initFaqs();

    // Add a single resize handler
    window.addEventListener('resize', this.debounce(this.handleResize, 250));
  }

  // Initialize product accordions
  initProductAccordions() {
    this.productAccordions = document.querySelectorAll('.product__accordion');
    const isMobile = window.innerWidth <= 768;

    this.productAccordions.forEach((accordion) => {
      const trigger = accordion.querySelector('[data-collapsible-trigger]');
      const container = accordion.querySelector('[data-collapsible-container]');
      const content = container?.querySelector('[data-collapsible-content]');

      if (trigger && container && content) {
        // Check if this accordion should be expanded by default based on device
        const expandDesktop = accordion.getAttribute('data-expand-desktop') === 'true';
        const expandMobile = accordion.getAttribute('data-expand-mobile') === 'true';
        // const divider = accordion.querySelector('.product__accordion__title__divider');

        // Logic: Expand if matches current device setting
        const shouldExpand = (isMobile && expandMobile) || (!isMobile && expandDesktop);

        if (shouldExpand) {
          // Set expanded state
          accordion.classList.add('is-expanded');
          trigger.setAttribute('aria-expanded', 'true');
          trigger.classList.add('is-expanded');
          container.classList.add('is-expanded');

          // Set height to content height
          // Use 'auto' initially to allow content to dictate height, but calculate for transitions
          container.style.height = 'auto'; // content.offsetHeight + 'px'; 
          
          // Request animation frame to set fixed height for animation support later if needed,
          // usually setting to auto is fine for initial load, but for transition we need explicit height.
          // However, if we leave it auto, toggle logic needs to handle 'auto' -> '0px'.
          // Existing logic sets pixel height. Let's stick to consistent behavior.
           requestAnimationFrame(() => {
              container.style.height = content.offsetHeight + 'px';
           });

          // Show divider logic (assuming user wants this synced)
          // The CSS handles divider opacity often, but let's keep JS logic consistent
           const divider = accordion.querySelector('.product__accordion__title__divider');
           if (divider) {
              divider.style.opacity = '1';
              divider.style.maxHeight = '1.5px';
              divider.style.marginTop = '15px';
              divider.style.marginBottom = '15px';
           }

        } else {
          // Set collapsed state
          accordion.classList.remove('is-expanded');
          trigger.setAttribute('aria-expanded', 'false');
          trigger.classList.remove('is-expanded');
          container.classList.remove('is-expanded');

          // Set height to 0 for collapsed state
          container.style.height = '0px';

             const divider = accordion.querySelector('.product__accordion__title__divider');
             if (divider) {
                divider.style.opacity = '0';
                divider.style.maxHeight = '0';
                divider.style.marginTop = '0';
                divider.style.marginBottom = '0';
             }
        }

        // Replace with new trigger to avoid default handlers
        const newTrigger = trigger.cloneNode(true);
        trigger.parentNode.replaceChild(newTrigger, trigger);

        // Add click handler
        newTrigger.addEventListener('click', this.handleAccordionClick);
      }
    });
  }

  // Initialize FAQ accordions
  initFaqs() {
    this.faqButtons = document.querySelectorAll('.product__accordion__faqs button');
    this.faqContents = document.querySelectorAll('.product__accordion__faqs__content');

    // Set initial heights for all FAQ contents
    this.faqContents.forEach(content => {
      content.style.height = '0';
    });

    // Add click handlers to FAQ buttons - DON'T USE this.handleFaqClick directly
    this.faqButtons.forEach(button => {
      button.addEventListener('click', function () {
        // 'this' now correctly refers to the button element
        const content = this.nextElementSibling;
        const isCollapsed = this.classList.contains('collapsed');

        // Find parent containers
        const faqList = this.closest('.product__accordion__faqs');
        const accordionContent = faqList?.closest('[data-collapsible-container]');
        const accordionInner = faqList?.closest('[data-collapsible-content]');

        // Toggle state
        if (isCollapsed) {
          this.classList.remove('collapsed');
          this.setAttribute('aria-expanded', 'true');
          content.style.height = content.scrollHeight + 'px';
        } else {
          this.classList.add('collapsed');
          this.setAttribute('aria-expanded', 'false');
          content.style.height = '0';
        }

        // Update parent container height
        if (accordionContent && accordionInner) {
          setTimeout(() => {
            requestAnimationFrame(() => {
              const originalHeight = accordionContent.style.height;
              accordionContent.style.height = 'auto';

              const newHeight = accordionInner.offsetHeight;
              accordionContent.style.height = originalHeight;

              // Force reflow
              void accordionContent.offsetHeight;

              // Set new height
              accordionContent.style.height = newHeight + 'px';
            });
          }, 350);
        }
      });
    });
  }

  // Handle accordion click events
  handleAccordionClick(event) {
    const accordion = event.target.closest('.product__accordion');
    if (!accordion) return;

    // Stop event propagation and prevent other handlers from being called
    event.stopPropagation();
    event.stopImmediatePropagation();
    event.preventDefault();

    // Critical: Remove default CSS-based expansion classes on first interaction
    // allowing JS state (is-expanded) to take full control without CSS interference.
    if (accordion.classList.contains('is-expanded-desktop')) {
        accordion.classList.remove('is-expanded-desktop');
    }
    if (accordion.classList.contains('is-expanded-mobile')) {
        accordion.classList.remove('is-expanded-mobile');
    }

    const trigger = event.target.closest('[data-collapsible-trigger]');
    const containerId = trigger?.getAttribute('aria-controls');
    // Avoid `getElementById()` because this theme can render the same accordion twice
    // (desktop-only + mobile-only), creating duplicate IDs in the DOM. Always scope the
    // lookup to the clicked accordion instance.
    let container = null;
    if (containerId) {
      container = accordion.querySelector(`[id="${containerId}"]`);
    }
    if (!container) {
      container = accordion.querySelector('[data-collapsible-container]');
    }

    if (trigger && container) {
      const isExpanded = trigger.getAttribute('aria-expanded') === 'true';
      const content = container.querySelector('[data-collapsible-content]');
      const isMobile = window.innerWidth <= 768;

      const divider = accordion.querySelector('.product__accordion__title__divider');

      // Track user interaction on mobile
      if (isMobile) {
        if (isExpanded) {
          // User is closing accordion, remove the flag
          delete accordion.dataset.userOpened;
        } else {
          // User is opening accordion, set the flag
          accordion.dataset.userOpened = 'true';
        }
      }

      // Toggle expanded state
      trigger.setAttribute('aria-expanded', (!isExpanded).toString());
      trigger.classList.toggle('is-expanded');
      container.classList.toggle('is-expanded');
      accordion.classList.toggle('is-expanded');

      // Animate height
      container.style.height = isExpanded ? '0px' : content.offsetHeight + 'px';

      // Toggle divider visibility
      if (divider) {
        if (isExpanded) {
          divider.style.opacity = '0';
          divider.style.maxHeight = '0';
          divider.style.marginTop = '0';
          divider.style.marginBottom = '0';
        } else {
          divider.style.opacity = '1';
          divider.style.maxHeight = '1.5px';
          divider.style.marginTop = '15px';
          divider.style.marginBottom = '15px';
        }
      }
    }

    return false;
  }

  // Handle FAQ click events
  handleFaqClick() {
    const button = this;
    const content = button.nextElementSibling;
    const isCollapsed = button.classList.contains('collapsed');

    // Find parent containers
    const faqList = button.closest('.product__accordion__faqs');
    const accordionContent = faqList?.closest('[data-collapsible-container]');
    const accordionInner = faqList?.closest('[data-collapsible-content]');

    // Toggle state
    if (isCollapsed) {
      button.classList.remove('collapsed');
      button.setAttribute('aria-expanded', 'true');
      content.style.height = content.scrollHeight + 'px';
    } else {
      button.classList.add('collapsed');
      button.setAttribute('aria-expanded', 'false');
      content.style.height = '0';
    }

    // Update parent container height
    if (accordionContent && accordionInner) {
      // Use requestAnimationFrame for better performance
      setTimeout(() => {
        requestAnimationFrame(() => {
          const originalHeight = accordionContent.style.height;
          accordionContent.style.height = 'auto';

          const newHeight = accordionInner.offsetHeight;
          accordionContent.style.height = originalHeight;

          // Force reflow
          void accordionContent.offsetHeight;

          // Set new height
          accordionContent.style.height = newHeight + 'px';
        });
      }, 350);
    }
  }

  // Handle window resize events
  handleResize() {
    // Re-initialize accordions - keep current state, just update heights for expanded ones
    this.productAccordions?.forEach((accordion) => {
      const trigger = accordion.querySelector('[data-collapsible-trigger]');
      const container = accordion.querySelector('[data-collapsible-container]');
      const content = container?.querySelector('[data-collapsible-content]');

      if (trigger && container && content) {
        // Only update height for expanded accordions
        const isExpanded = accordion.classList.contains('is-expanded');
        if (isExpanded) {
          container.style.height = content.offsetHeight + 'px';
        }
      }
    });

    // Update FAQ content heights
    document.querySelectorAll('.product__accordion__faqs button:not(.collapsed)').forEach(button => {
      const content = button.nextElementSibling;
      if (!content) return;

      // Update height without animation
      content.style.transition = 'none';
      content.style.height = 'auto';
      const newHeight = content.scrollHeight;
      content.style.height = newHeight + 'px';
      void content.offsetHeight;
      content.style.transition = '';
    });

    // Update accordion container heights
    document.querySelectorAll('[data-collapsible-container].is-expanded').forEach(container => {
      const content = container.querySelector('[data-collapsible-content]');
      if (!content) return;

      // Update height without animation
      container.style.transition = 'none';
      container.style.height = content.offsetHeight + 'px';
      void container.offsetHeight;
      container.style.transition = '';
    });
  }

  // Utility: Debounce function
  debounce(func, wait) {
    let timeout;
    return () => {
      const context = this;
      const args = arguments;
      clearTimeout(timeout);
      timeout = setTimeout(() => func.apply(context, args), wait);
    };
  }
}

/**
 * SKU Handler Class
 * Handles SKU display and updates when variants change
 */
class SKUHandler {
  constructor() {
    // Check if we're on a product page
    if (!document.body.classList.contains('template-product')) return;

    // Initialize properties
    this.skuElements = document.querySelectorAll('[data-sku]');
    if (!this.skuElements.length) return;

    // Find product JSON
    const productJSON = document.querySelector('[data-product-json]');
    if (!productJSON) return;

    try {
      this.product = JSON.parse(productJSON.innerHTML);

      // Initialize SKU on page load
      this.initializeSKU();

      // Listen for variant changes
      this.setupEventListeners();
    } catch (e) {
      console.error('Error parsing product JSON:', e);
    }
  }

  /**
   * Initialize SKU display with the first available variant
   */
  initializeSKU() {
    const initialVariant = this.product.selected_or_first_available_variant;
    if (!initialVariant) return;

    this.updateSKU(initialVariant);
  }

  /**
   * Set up event listeners for variant changes
   */
  setupEventListeners() {
    // Listen for variant changes on the select element
    const variantSelect = document.querySelector('[data-product-select]');
    if (variantSelect) {
      variantSelect.addEventListener('change', (e) => {
        const variantId = Number(e.target.value);
        const variant = this.product.variants.find(v => v.id === variantId);
        if (variant) this.updateSKU(variant);
      });
    }

    // Listen for custom variant change events
    document.addEventListener('variant:change', (e) => {
      if (e.detail && e.detail.variant) {
        this.updateSKU(e.detail.variant);
      }
    });

    // Listen for click events on swatch elements
    const swatchElements = document.querySelectorAll('[data-swatch-variant]');
    swatchElements.forEach(swatch => {
      swatch.addEventListener('click', () => {
        // Allow time for the theme's JS to update the variant
        setTimeout(() => {
          const currentId = Number(document.querySelector('[name="id"]').value);
          const variant = this.product.variants.find(v => v.id === currentId);
          if (variant) this.updateSKU(variant);
        }, 100);
      });
    });
  }

  /**
   * Update SKU display based on variant
   * @param {Object} variant - The current variant object
   */
  updateSKU(variant) {
    this.skuElements.forEach(element => {
      // Add updating class for smooth transition
      element.classList.add('updating');

      setTimeout(() => {
        if (variant && variant.sku) {
          element.innerHTML = variant.sku;
          element.classList.remove('hidden');
        } else {
          element.innerHTML = '';
          element.classList.add('hidden');
        }

        // Remove updating class to show the new content
        element.classList.remove('updating');
      }, 150); // Slight delay for animation
    });
  }
}

class BlogsCollection extends HTMLElement {
  constructor() {
    super();
    // Wait for DOM to fully load before initializing
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        this.initializeButtons();
        this.initializeMobileScroll();
        this.initializeAllCategory();
      });
    } else {
      this.initializeButtons();
      this.initializeMobileScroll();
      this.initializeAllCategory();
    }
  }

  // Initialize mobile scrolling
  initializeMobileScroll() {
    // Check if it is a mobile device
    if (window.innerWidth <= 767) {
      const categoriesContainer = this.querySelector('.categories-collapse');
      if (categoriesContainer && categoriesContainer.children.length >= 3) {
        // Scroll to the activated button
        const activeButton = categoriesContainer.querySelector('.category-button.active');
        if (activeButton) {
          // Calculate the scroll position to center the activation button
          const containerWidth = categoriesContainer.offsetWidth;
          const buttonLeft = activeButton.offsetLeft;
          const buttonWidth = activeButton.offsetWidth;
          const scrollPosition = buttonLeft - containerWidth / 2 + buttonWidth / 2;

          // Setting the scroll position
          setTimeout(() => {
            categoriesContainer.scrollLeft = scrollPosition;
          }, 100);
        }
      }
    }
  }

  // Initialize "All" category - sort articles by published date
  async initializeAllCategory() {
    const allContainer = this.querySelector('[data-all-articles-container]');
    const allContent = this.querySelector('.blog-category-content[data-blog-handle="all"]');

    if (!allContainer) return;

    // Get all article items from initial load
    const articleItems = Array.from(allContainer.querySelectorAll('.all-article-item'));

    if (articleItems.length === 0) return;

    // Extract blog handles
    const blogHandles = new Set();
    articleItems.forEach(item => {
      const blogHandle = item.getAttribute('data-blog-handle');
      if (blogHandle) {
        blogHandles.add(blogHandle);
      }
    });

    // Store all articles (will be populated with AJAX data)
    this.allArticles = [...articleItems];
    this.articlesPerPage = 21; // Number of articles to show per page
    this.currentPage = 1;

    // Show loading indicator
    const loadingIndicator = this.querySelector('.all-category-loading');
    if (loadingIndicator && allContent && allContent.classList.contains('active')) {
      loadingIndicator.style.display = 'block';
    }

    // Fetch all articles from all blogs
    await this.fetchAllBlogArticles(Array.from(blogHandles));

    // Hide loading indicator
    if (loadingIndicator) {
      loadingIndicator.style.display = 'none';
    }

    // Sort all articles by published date (newest first)
    this.allArticles.sort((a, b) => {
      const dateA = new Date(a.getAttribute('data-published-at'));
      const dateB = new Date(b.getAttribute('data-published-at'));
      return dateB - dateA;
    });

    // Remove duplicates based on article ID
    const seenIds = new Set();
    this.allArticles = this.allArticles.filter(item => {
      const articleId = item.getAttribute('data-article-id');
      if (seenIds.has(articleId)) {
        return false;
      }
      seenIds.add(articleId);
      return true;
    });

    // Display first page
    this.displayArticlesPage(1);

    // Initialize "Load More" button
    this.initializeLoadMoreButton();

    // Force browser to recalculate styles and ensure content is visible
    if (allContent) {
      // Force reflow to ensure styles are applied
      void allContent.offsetHeight;

      // Only ensure active class if "All" is actually the active category
      // Don't force it active if another category is selected
      const allButton = this.querySelector('.category-button[data-blog-handle="all"]');
      if (allButton && allButton.classList.contains('active') && !allContent.classList.contains('active')) {
        allContent.classList.add('active');
      }
    }

    // 尝试多种方式触发 AOS 刷新
    // 方式1：直接调用全局 AOS
    if (typeof AOS !== 'undefined' && AOS.refresh) {
      setTimeout(() => {
        AOS.refresh();
      }, 100);
    }

    // 方式2：通过 window.AOS 调用
    if (typeof window.AOS !== 'undefined' && window.AOS.refresh) {
      setTimeout(() => {
        window.AOS.refresh();
      }, 100);
    }

    // 方式3：触发滚动事件来让 AOS 重新检测元素
    setTimeout(() => {
      window.dispatchEvent(new Event('scroll'));
    }, 150);
  }

  // Fetch all articles from all blogs
  async fetchAllBlogArticles(blogHandles) {
    // Fetch articles from each blog sequentially to detect when to stop
    for (const blogHandle of blogHandles) {
      await this.fetchBlogPages(blogHandle);
    }
  }

  // Fetch pages for a single blog until no more articles
  async fetchBlogPages(blogHandle) {
    let page = 2; // Start from page 2 (page 1 is already loaded)
    let hasMorePages = true;
    const articlesPerPage = 50; // Shopify's pagination limit

    while (hasMorePages && page <= 20) { // Max 20 pages to prevent infinite loops
      try {
        const response = await fetch(`/blogs/${blogHandle}?page=${page}&view=ajax`);

        if (!response.ok) {
          console.warn(`Failed to fetch page ${page} for blog ${blogHandle}`);
          break;
        }

        const html = await response.text();
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        const articles = doc.querySelectorAll('.blog__post-item');

        // If no articles found, we've reached the end
        if (articles.length === 0) {
          hasMorePages = false;
          break;
        }

        // Process articles
        articles.forEach(article => {
          const articleId = article.getAttribute('data-article-id');
          const publishedAt = article.getAttribute('data-published-at');

          const wrapper = document.createElement('div');
          wrapper.className = 'all-article-item';
          wrapper.setAttribute('data-published-at', publishedAt || new Date().toISOString());
          wrapper.setAttribute('data-article-id', articleId || '');
          wrapper.setAttribute('data-blog-handle', blogHandle);
          wrapper.appendChild(article.cloneNode(true));

          this.allArticles.push(wrapper);
        });

        // If we got fewer articles than the page size, this is the last page
        if (articles.length < articlesPerPage) {
          hasMorePages = false;
        }

        page++;
      } catch (error) {
        console.error(`Error fetching ${blogHandle} page ${page}:`, error);
        break;
      }
    }
  }

  // Display a specific page of articles
  displayArticlesPage(page) {
    const allContainer = this.querySelector('[data-all-articles-container]');
    if (!allContainer) return;

    const startIndex = (page - 1) * this.articlesPerPage;
    const endIndex = startIndex + this.articlesPerPage;
    const articlesToShow = this.allArticles.slice(startIndex, endIndex);

    // Clear container
    allContainer.innerHTML = '';

    // Add articles for this page
    articlesToShow.forEach((item, index) => {
      const blogPostItem = item.querySelector('.blog__post-item');
      if (blogPostItem) {
        const newDelay = 150 * (index + 1);
        blogPostItem.setAttribute('data-aos-delay', newDelay);
        blogPostItem.classList.add('aos-animate');
      }
      allContainer.appendChild(item);
    });

    // Update current page
    this.currentPage = page;

    // Refresh AOS
    if (typeof AOS !== 'undefined' && AOS.refresh) {
      setTimeout(() => AOS.refresh(), 100);
    }

    // Update load more button visibility
    this.updateLoadMoreButton();
  }

  // Update pagination buttons and info
  updateLoadMoreButton() {
    const loadMoreContainer = this.querySelector('.all-category-load-more');
    const prevButton = this.querySelector('.pagination-prev-button');
    const prevPlaceholder = this.querySelector('.pagination-prev-placeholder');
    const nextButton = this.querySelector('.pagination-next-button');
    const currentPageSpan = this.querySelector('.pagination-info-current');
    const totalPagesSpan = this.querySelector('.pagination-info-total');

    if (!loadMoreContainer) return;

    const totalPages = Math.ceil(this.allArticles.length / this.articlesPerPage);
    const hasPrevPage = this.currentPage > 1;
    const hasNextPage = this.currentPage < totalPages;

    // Show/hide container
    if (totalPages > 1) {
      loadMoreContainer.style.display = 'flex';
    } else {
      loadMoreContainer.style.display = 'none';
      return;
    }

    // Update previous button
    if (prevButton && prevPlaceholder) {
      if (hasPrevPage) {
        prevButton.style.display = 'flex';
        prevPlaceholder.style.display = 'none';
      } else {
        prevButton.style.display = 'none';
        prevPlaceholder.style.display = 'block';
      }
    }

    // Update next button
    if (nextButton) {
      if (hasNextPage) {
        nextButton.disabled = false;
        nextButton.classList.remove('disabled');
      } else {
        nextButton.disabled = true;
        nextButton.classList.add('disabled');
      }
    }

    // Update pagination info
    if (currentPageSpan) {
      currentPageSpan.textContent = this.currentPage;
    }
    if (totalPagesSpan) {
      totalPagesSpan.textContent = totalPages;
    }
  }

  // Initialize pagination buttons for All category
  initializeLoadMoreButton() {
    const loadMoreContainer = this.querySelector('.all-category-load-more');
    const prevButton = this.querySelector('.pagination-prev-button');
    const nextButton = this.querySelector('.pagination-next-button');

    if (!loadMoreContainer) return;

    // Show the pagination only when "All" category is active
    const allContent = this.querySelector('.blog-category-content[data-blog-handle="all"]');
    if (allContent && allContent.classList.contains('active')) {
      this.updateLoadMoreButton();
    }

    // Handle previous page button click
    if (prevButton) {
      prevButton.addEventListener('click', (e) => {
        e.preventDefault();

        if (this.currentPage > 1) {
          // Load previous page
          this.displayArticlesPage(this.currentPage - 1);

          // Scroll to top of content
          setTimeout(() => {
            const featuredBlog = this.querySelector('.featured-blog');
            if (featuredBlog) {
              featuredBlog.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
          }, 100);
        }
      });
    }

    // Handle next page button click
    if (nextButton) {
      nextButton.addEventListener('click', (e) => {
        e.preventDefault();

        // Prevent multiple clicks
        if (nextButton.getAttribute('data-loading') === 'true') {
          return;
        }

        const totalPages = Math.ceil(this.allArticles.length / this.articlesPerPage);
        if (this.currentPage < totalPages) {
          // Load next page
          this.displayArticlesPage(this.currentPage + 1);

          // Scroll to top of content
          setTimeout(() => {
            const featuredBlog = this.querySelector('.featured-blog');
            if (featuredBlog) {
              featuredBlog.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
          }, 100);
        }
      });
    }
  }

  initializeButtons() {
    // Handle category buttons
    this.querySelectorAll('.category-button').forEach((button) => {
      button.addEventListener(
        'click',
        (e) => {
          e.preventDefault();
          e.stopPropagation();

          // If already active, do nothing
          if (button.classList.contains('active')) {
            return false;
          }

          // Update button states
          this.querySelectorAll('.category-button').forEach((btn) => {
            btn.classList.remove('active');
          });
          button.classList.add('active');

          // Get blog handle
          const blogHandle = button.getAttribute('data-blog-handle');

          // Check if there's a page parameter in the URL
          const currentUrl = new URL(window.location);
          const hasPageParam = currentUrl.searchParams.has('page');

          // If there's a page parameter, we need to reload the page to reset pagination
          // Otherwise, the new category will show the wrong page
          if (hasPageParam) {
            // Build new URL with category but without page parameter
            const url = new URL(window.location);
            if (blogHandle === 'all') {
              url.searchParams.delete('category');
            } else {
              url.searchParams.set('category', blogHandle);
            }
            url.searchParams.delete('page');
            // Reload page with new URL
            window.location.href = url.toString();
            return false;
          }

          // No page parameter, safe to switch without reload
          const url = new URL(window.location);
          if (blogHandle === 'all') {
            // Remove category parameter for "All"
            url.searchParams.delete('category');
          } else {
            // Set category parameter
            url.searchParams.set('category', blogHandle);
          }
          window.history.pushState({}, '', url);

          // Get current active content
          const activeContent = this.querySelector('.blog-category-content.active');
          const targetContent = this.querySelector(`.blog-category-content[data-blog-handle="${blogHandle}"]`);

          if (!targetContent) {
            console.error('Target content does not exist:', blogHandle);
            return false;
          }

          // Show/hide "Load More" button based on category
          const loadMoreContainer = this.querySelector('.all-category-load-more');
          if (loadMoreContainer) {
            if (blogHandle === 'all') {
              // Check if there are more pages to show
              if (this.allArticles && this.currentPage) {
                this.updateLoadMoreButton();
              } else {
                loadMoreContainer.style.display = 'flex';
              }
            } else {
              loadMoreContainer.style.display = 'none';
            }
          }

          // If there is currently active content, fade it out first
          if (activeContent) {
            activeContent.classList.remove('active');

            // Wait for fade-out animation to complete before showing new content
            setTimeout(() => {
              // Show target content
              targetContent.classList.add('active');

              // Scroll to top of the content area smoothly
              const featuredBlog = this.querySelector('.featured-blog');
              if (featuredBlog) {
                featuredBlog.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }
            }, 300); // 300ms is approximately the fade-out animation time
          } else {
            // No currently active content, show target content directly
            targetContent.classList.add('active');
          }

          return false;
        },
        { passive: false }
      );
    });

    // Initialize the currently selected category content
    // Check current URL to determine which category should be active
    const urlParams = new URLSearchParams(window.location.search);
    const categoryParam = urlParams.get('category');
    let matchedBlogHandle = null;

    // Check if URL has a category parameter
    if (categoryParam) {
      // Verify the category exists in our buttons
      const categoryButton = this.querySelector(`.category-button[data-blog-handle="${categoryParam}"]`);
      if (categoryButton) {
        matchedBlogHandle = categoryParam;
      }
    }

    // If URL matches a specific category, activate it; otherwise default to "All"
    if (matchedBlogHandle) {
      // Activate the matched category
      const matchedButton = this.querySelector(`.category-button[data-blog-handle="${matchedBlogHandle}"]`);
      const matchedContent = this.querySelector(`.blog-category-content[data-blog-handle="${matchedBlogHandle}"]`);

      if (matchedButton && matchedContent) {
        this.querySelectorAll('.category-button').forEach(btn => btn.classList.remove('active'));
        this.querySelectorAll('.blog-category-content').forEach(content => content.classList.remove('active'));

        matchedButton.classList.add('active');
        matchedContent.classList.add('active');

        // Force reflow to ensure the content is visible
        void matchedContent.offsetHeight;
      }
    } else {
      // Default to "All" category
      const allButton = this.querySelector('.category-button[data-blog-handle="all"]');
      const allContent = this.querySelector('.blog-category-content[data-blog-handle="all"]');

      if (allButton && allContent) {
        // Check if "All" button is already active (from server-side rendering)
        // Only reset if needed to avoid unnecessary DOM manipulation
        const isAllButtonActive = allButton.classList.contains('active');
        const isAllContentActive = allContent.classList.contains('active');

        if (!isAllButtonActive || !isAllContentActive) {
          // Ensure "All" is active by default
          this.querySelectorAll('.category-button').forEach(btn => btn.classList.remove('active'));
          this.querySelectorAll('.blog-category-content').forEach(content => content.classList.remove('active'));

          allButton.classList.add('active');
          allContent.classList.add('active');
        }

        // Force reflow to ensure the content is visible
        void allContent.offsetHeight;
      } else {
        // Fallback to original logic if "All" doesn't exist
        const activeButton = this.querySelector('.category-button.active');
        if (activeButton) {
          const blogHandle = activeButton.getAttribute('data-blog-handle');
          const targetContent = this.querySelector(`.blog-category-content[data-blog-handle="${blogHandle}"]`);
          if (targetContent) {
            targetContent.classList.add('active');
          }
        } else {
          // If no active button, activate the first one by default
          const firstButton = this.querySelector('.category-button');
          if (firstButton) {
            firstButton.classList.add('active');
            const blogHandle = firstButton.getAttribute('data-blog-handle');
            const targetContent = this.querySelector(`.blog-category-content[data-blog-handle="${blogHandle}"]`);
            if (targetContent) {
              targetContent.classList.add('active');
            }
          }
        }
      }
    }
  }
}

// Ensure custom element is defined after DOM is loaded
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    customElements.define('blogs-collection', BlogsCollection);
  });
} else {
  customElements.define('blogs-collection', BlogsCollection);
}

// Initialize the SKU Handler
new SKUHandler();
// Initialize the ProductAccordions
new ProductAccordions();
// Initialize the BlogsCollection
new BlogsCollection();
// Mobile ToggleEllipsis Handler
class MobileToggleEllipsisHandler {
  constructor() {
    this.isMobile = window.innerWidth <= 768;
    this.initialized = false;

    // Wait for DOM and custom elements to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        setTimeout(() => this.init(), 500);
      });
    } else {
      setTimeout(() => this.init(), 500);
    }

    this.handleResize();
  }

  init() {
    // Find all toggle-ellipsis elements with mobile-disabled attribute
    const toggleElements = document.querySelectorAll('toggle-ellipsis[data-mobile-disabled="true"]');

    if (toggleElements.length === 0 && !this.initialized) {
      // Retry after a short delay if elements aren't found yet
      setTimeout(() => this.init(), 1000);
      return;
    }

    this.initialized = true;

    toggleElements.forEach(element => {
      this.handleToggleEllipsis(element);
    });
  }

  handleToggleEllipsis(element) {
    if (this.isMobile) {
      this.disableToggleEllipsis(element);
    } else {
      this.enableToggleEllipsis(element);
    }
  }

  disableToggleEllipsis(element) {
    // For mobile, rely primarily on CSS but ensure no conflicting JS styles
    element.style.removeProperty('height');
    element.style.removeProperty('overflow');
    element.style.removeProperty('--height');

    // Mark as disabled to prevent initialization
    element.setAttribute('data-disabled', 'true');
    element.classList.remove('is-enabled', 'is-open');

    // Disable the button click functionality
    const button = element.querySelector('[data-button]');
    if (button) {
      button.style.pointerEvents = 'none';
      button.setAttribute('disabled', 'true');
    }
  }

  enableToggleEllipsis(element) {
    // Remove disabled state and let CSS/JS handle the normal behavior
    element.removeAttribute('data-disabled');
    element.style.removeProperty('height');
    element.style.removeProperty('overflow');

    // Re-enable the button click functionality
    const button = element.querySelector('[data-button]');
    if (button) {
      button.style.pointerEvents = '';
      button.removeAttribute('disabled');
    }

    // Trigger the original toggle ellipsis initialization if available
    if (element.toggleActions && typeof element.toggleActions === 'function') {
      element.toggleActions();
    }
  }

  handleResize() {
    window.addEventListener('resize', () => {
      const wasMobile = this.isMobile;
      this.isMobile = window.innerWidth <= 768;

      // Only re-initialize if mobile state changed
      if (wasMobile !== this.isMobile) {
        this.init();
      }
    });
  }
}

// Initialize the MobileToggleEllipsisHandler
new MobileToggleEllipsisHandler();

// Detect mobile device
function isMobileDevice() {
  return (
    window.innerWidth <= 767 ||
    /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
  );
}

// Hide Shopify Chat on cart open - only hide when cart popup is open
function hideShopifyChatOnCartOpen() {
  const isMobile = isMobileDevice();

  // Only execute on mobile devices
  if (isMobile) {
    // Shopify Chat related selectors
    const chatSelectors = [
      'inbox-online-store-chat',
      '#shopify-chat',
      '#ShopifyChat',
      '[id="ShopifyChat"]',
      '[title="Shopify online store chat"]',
    ];

    // Create and inject CSS styles - only hide when cart popup is open
    const styleElement = document.createElement('style');
    styleElement.id = 'hide-shopify-chat-cart';
    styleElement.textContent = `
      /* Hide chat button only when cart popup is open on mobile devices */
      @media (max-width: 767px) {
        /* Only hide when cart popup is open */
        body.cart-open inbox-online-store-chat,
        body.cart-open #shopify-chat,
        body.cart-open #ShopifyChat,
        body.cart-open [title="Shopify online store chat"],
        /* Or when cart drawer is open */
        .cart-drawer--open ~ inbox-online-store-chat,
        .cart-drawer--open ~ #shopify-chat,
        .cart-drawer--open ~ #ShopifyChat,
        .cart-drawer--open ~ [title="Shopify online store chat"],
        /* Cart related popups */
        .drawer--cart ~ inbox-online-store-chat,
        .drawer--cart ~ #shopify-chat,
        .drawer--cart ~ #ShopifyChat,
        .drawer--cart ~ [title="Shopify online store chat"] {
          display: none !important;
          visibility: hidden !important;
          opacity: 0 !important;
          pointer-events: none !important;
        }
      }
    `;

    // Add style element if it doesn't exist
    if (!document.getElementById('hide-shopify-chat-cart')) {
      document.head.appendChild(styleElement);
    }

    // Check if cart popup is open
    function isCartOpen() {
      return (
        document.body.classList.contains('cart-open') ||
        document.querySelector('.cart-drawer--open') ||
        document.querySelector('.drawer--cart') ||
        document.querySelector('[data-cart-drawer][class*="open"]') ||
        document.querySelector('#cart-drawer[style*="transform: translateX(0"]') ||
        document.querySelector('.cart-sidebar[class*="open"]')
      );
    }

    // Find and control chat elements show/hide
    function toggleChatElements() {
      const shouldHide = isCartOpen();

      chatSelectors.forEach((selector) => {
        const chatElements = document.querySelectorAll(selector);
        if (chatElements.length) {
          chatElements.forEach((el) => {
            if (shouldHide) {
              // Only hide when cart is open
              el.style.display = 'none';
              el.style.visibility = 'hidden';
              el.style.opacity = '0';
              el.style.pointerEvents = 'none';
            } else {
              // Restore visibility when cart is closed
              el.style.display = '';
              el.style.visibility = '';
              el.style.opacity = '';
              el.style.pointerEvents = '';
            }
          });
        }
      });
    }

    // Listen for DOM changes
    const observer = new MutationObserver(function (mutations) {
      mutations.forEach(function (mutation) {
        // Listen for attribute changes (class changes)
        if (
          mutation.type === 'attributes' &&
          (mutation.attributeName === 'class' || mutation.attributeName === 'style')
        ) {
          // Check if it's body class change or cart related element change
          if (
            mutation.target === document.body ||
            mutation.target.classList.contains('cart-drawer') ||
            mutation.target.classList.contains('drawer') ||
            mutation.target.id === 'cart-drawer'
          ) {
            setTimeout(toggleChatElements, 100);
          }
        }

        // Listen for newly added chat elements
        if (mutation.addedNodes && mutation.addedNodes.length > 0) {
          for (let i = 0; i < mutation.addedNodes.length; i++) {
            const node = mutation.addedNodes[i];
            if (
              node.nodeName &&
              (node.nodeName.toLowerCase() === 'inbox-online-store-chat' ||
                node.id === 'ShopifyChat' ||
                node.id === 'shopify-chat')
            ) {
              setTimeout(toggleChatElements, 200);
            }
          }
        }
      });
    });

    // Start listening
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'style'],
    });

    // Listen for changes to cart related elements
    const cartElements = document.querySelectorAll('[data-cart-drawer], #cart-drawer, .cart-drawer, .drawer');
    cartElements.forEach(element => {
      observer.observe(element, {
        attributes: true,
        attributeFilter: ['class', 'style'],
      });
    });

    // Listen for cart button click events
    document.addEventListener('click', function (e) {
      // Find cart trigger buttons
      if (
        e.target.closest('[data-cart-toggle]') ||
        e.target.closest('.cart-toggle') ||
        e.target.closest('.header__cart') ||
        e.target.closest('[href="/cart"]')
      ) {
        setTimeout(toggleChatElements, 300);
      }
    });

    // Initial check - ensure chat is shown on normal pages
    setTimeout(() => {
      if (!isCartOpen()) {
        // Ensure chat is shown on normal pages
        chatSelectors.forEach((selector) => {
          const chatElements = document.querySelectorAll(selector);
          chatElements.forEach((el) => {
            el.style.display = '';
            el.style.visibility = '';
            el.style.opacity = '';
            el.style.pointerEvents = '';
          });
        });
      }
    }, 1000);

    console.log('✅ Shopify Chat control function enabled (only hide when cart popup is open)');
  }
}

// Hide Shopify Chat on quick-add interface open
function hideShopifyChatOnQuickAdd() {
  const isMobile = isMobileDevice();

  if (isMobile) {


    // Execute on all devices, but with different behaviors
    const chatSelectors = [
      'inbox-online-store-chat',
      '#shopify-chat',
      '#ShopifyChat',
      '[id="ShopifyChat"]',
      '[title="Shopify online store chat"]',
    ];

    // Function to hide chat elements
    function hideChatElements() {
      chatSelectors.forEach((selector) => {
        const chatElements = document.querySelectorAll(selector);
        if (chatElements.length) {
          chatElements.forEach((el) => {
            el.setAttribute('style', 'display: none;');
            el.setAttribute('data-quickadd-hidden', 'true');
          });
        }
      });
    }

    // Function to show chat elements
    function showChatElements() {
      chatSelectors.forEach((selector) => {
        const chatElements = document.querySelectorAll(selector);
        chatElements.forEach((el) => {
          if (el.getAttribute('data-quickadd-hidden') === 'true') {
            el.setAttribute('style', "position: fixed; bottom: 120px; z-index: 2147483647; right: 16px; height: 65px;");
            el.removeAttribute('data-quickadd-hidden');
          }
        });
      });
    }

    // Check if quick-add interface is open
    function isQuickAddOpen() {
      // 检查是否有可见的快速查看元素
      const quickviewElements = document.querySelectorAll('[id^="quickview-"]');
      let hasVisibleQuickview = false;

      quickviewElements.forEach(element => {
        const computedStyle = getComputedStyle(element);
        if (computedStyle.display !== 'none' &&
          computedStyle.visibility !== 'hidden' &&
          element.offsetParent !== null) {
          hasVisibleQuickview = true;
        }
      });

      return (
        hasVisibleQuickview ||
        // 原有的选择器
        document.querySelector('.drawer--quick-add.drawer--is-open') ||
        document.querySelector('[data-quick-add-modal].is-open') ||
        document.querySelector('.quick-add-modal.active') ||
        document.querySelector('.product-popup.is-open') ||
        document.querySelector('.quick-view-popup.active') ||
        document.body.classList.contains('quick-add-open') ||
        document.body.classList.contains('modal-open') ||

        // 其他快速查看相关的选择器
        document.querySelector('.quickview.is-open') ||
        document.querySelector('.drawer--quickview.drawer--is-open') ||
        document.querySelector('[data-quickview-modal].is-open') ||
        document.querySelector('.product-quickview.active') ||
        document.body.classList.contains('quickview-open') ||

        // 检查抽屉状态
        document.querySelector('.drawer[id^="quickview-"]:not([style*="display: none"])') ||
        document.querySelector('.modal[id^="quickview-"]:not([style*="display: none"])') ||

        // 检查快速查看产品容器
        document.querySelector('[data-quickview-product]') ||
        document.querySelector('.quickview__product') ||

        // 检查主题特定的快速查看选择器
        document.querySelector('.product-quickview-container.active') ||
        document.querySelector('.quickview-container.is-visible')
      );
    }

    // Toggle chat elements based on quick-add state
    function toggleChatOnQuickAdd() {
      const shouldHide = isQuickAddOpen();

      if (shouldHide) {
        hideChatElements();
      } else {
        showChatElements();
      }
    }

    // Listen for quick-add button clicks
    document.addEventListener('click', function (e) {
      // Check for quick-add trigger buttons
      if (
        e.target.closest('[data-button-quick-view]') ||
        e.target.closest('.btn-quick-buy') ||
        e.target.closest('[data-quick-add]') ||
        e.target.closest('[data-modal-toggle]') ||

        // 添加快速查看相关的触发按钮
        e.target.closest('[data-quickview]') ||
        e.target.closest('[data-quickview-trigger]') ||
        e.target.closest('[data-button-quickview]') ||
        e.target.closest('.btn-quickview') ||
        e.target.closest('.quickview-trigger') ||
        e.target.closest('[href*="quickview"]') ||
        e.target.closest('[data-product-modal]')
      ) {
        setTimeout(() => {
          toggleChatOnQuickAdd();
          startStatusCheck(); // 开始状态检查
        }, 100);
      }

      // Check for modal close buttons
      if (
        e.target.closest('[data-modal-close]') ||
        e.target.closest('.drawer__close') ||
        e.target.closest('.modal__close') ||
        e.target.closest('.quick-add__close') ||

        // 添加快速查看关闭按钮
        e.target.closest('.quickview__close') ||
        e.target.closest('[data-quickview-close]') ||
        e.target.closest('.btn-quickview-close')
      ) {
        setTimeout(() => {
          toggleChatOnQuickAdd();
          // 确保聊天按钮重新显示
          setTimeout(showChatElements, 500);
        }, 300);
      }

      // Check for overlay/backdrop clicks
      if (
        e.target.classList.contains('drawer__underlay') ||
        e.target.classList.contains('modal__overlay') ||
        e.target.classList.contains('quick-add__overlay') ||

        // 添加快速查看遮罩层
        e.target.classList.contains('quickview__overlay') ||
        e.target.classList.contains('quickview-overlay')
      ) {
        setTimeout(() => {
          toggleChatOnQuickAdd();
          // 确保聊天按钮重新显示
          setTimeout(showChatElements, 500);
        }, 300);
      }
    });

    // Listen for ESC key to close quick-add
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') {
        setTimeout(() => {
          toggleChatOnQuickAdd();
          // 确保聊天按钮重新显示
          setTimeout(showChatElements, 500);
        }, 300);
      }
    });

    // Listen for DOM changes to detect quick-add state changes
    const observer = new MutationObserver(function (mutations) {
      mutations.forEach(function (mutation) {
        // Listen for class changes on body or quick-add elements
        if (
          mutation.type === 'attributes' &&
          (mutation.attributeName === 'class' || mutation.attributeName === 'style')
        ) {
          if (
            mutation.target === document.body ||
            mutation.target.classList.contains('drawer') ||
            mutation.target.classList.contains('modal') ||
            mutation.target.classList.contains('quick-add') ||
            mutation.target.classList.contains('product-popup') ||

            // 添加快速查看相关的元素监听
            mutation.target.classList.contains('quickview') ||
            mutation.target.classList.contains('product-quickview') ||
            mutation.target.id?.startsWith('quickview-')
          ) {
            setTimeout(toggleChatOnQuickAdd, 100);
          }
        }

        // Listen for newly added quick-add elements
        if (mutation.addedNodes && mutation.addedNodes.length > 0) {
          for (let i = 0; i < mutation.addedNodes.length; i++) {
            const node = mutation.addedNodes[i];
            if (
              node.nodeType === 1 && // Element node
              (node.classList?.contains('drawer--quick-add') ||
                node.classList?.contains('quick-add-modal') ||
                node.classList?.contains('product-popup') ||
                node.querySelector?.('[data-quick-add-modal]') ||

                // 添加快速查看相关的新增元素检测
                node.classList?.contains('quickview') ||
                node.classList?.contains('product-quickview') ||
                node.id?.startsWith('quickview-') ||
                node.querySelector?.('[id^="quickview-"]') ||
                node.querySelector?.('[data-quickview-modal]'))
            ) {
              setTimeout(toggleChatOnQuickAdd, 200);
            }
          }
        }

        // 监听元素被移除的情况
        if (mutation.removedNodes && mutation.removedNodes.length > 0) {
          for (let i = 0; i < mutation.removedNodes.length; i++) {
            const node = mutation.removedNodes[i];
            if (
              node.nodeType === 1 && // Element node
              (node.classList?.contains('quickview') ||
                node.classList?.contains('product-quickview') ||
                node.id?.startsWith('quickview-'))
            ) {
              // 当快速查看元素被移除时，延迟检查状态
              setTimeout(toggleChatOnQuickAdd, 300);
            }
          }
        }
      });
    });

    // Start observing
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'style'],
    });

    // 也观察特定的快速添加相关元素（如果存在）
    const quickAddElements = document.querySelectorAll('[data-quick-add-modal], .drawer--quick-add, .product-popup, .quick-add-modal, [id^="quickview-"], .quickview, .product-quickview');
    quickAddElements.forEach(element => {
      observer.observe(element, {
        attributes: true,
        attributeFilter: ['class', 'style'],
      });
    });

    // Initial check
    setTimeout(() => {
      window.lastQuickAddState = isQuickAddOpen();
      if (!window.lastQuickAddState) {
        showChatElements();
      }
    }, 1000);

    console.log('✅ Shopify Chat control for Quick-Add enabled');
  }
}

// Hide Shopify Chat when popup is open
function hideShopifyChatOnPopup() {
  const isMobile = isMobileDevice();

  if (isMobile) {
    const chatSelectors = [
      'inbox-online-store-chat',
      '#shopify-chat',
      '#ShopifyChat',
      '[id="ShopifyChat"]',
      '[title="Shopify online store chat"]',
    ];

    // Function to hide chat elements
    function hideChatElements() {
      chatSelectors.forEach((selector) => {
        const chatElements = document.querySelectorAll(selector);
        if (chatElements.length) {
          chatElements.forEach((el) => {
            el.setAttribute('style', 'display: none;');
            el.setAttribute('data-popup-hidden', 'true');
          });
        }
      });
    }

    // Function to show chat elements
    function showChatElements() {
      chatSelectors.forEach((selector) => {
        const chatElements = document.querySelectorAll(selector);
        chatElements.forEach((el) => {
          if (el.getAttribute('data-popup-hidden') === 'true') {
            el.setAttribute('style', "position: fixed; bottom: 120px; z-index: 2147483647; right: 16px; height: 65px;");
            el.removeAttribute('data-popup-hidden');
          }
        });
      });
    }

    // Check if any popup is open or expanded
    function isPopupOpen() {
      // 检查 popup--large 类型弹窗
      const largePopups = document.querySelectorAll('[data-large-promo-inner]');
      let hasVisibleLargePopup = false;

      largePopups.forEach(popup => {
        // 检查是否有 popup--visible 类
        if (popup.classList.contains('popup--visible')) {
          console.log("1");
          hasVisibleLargePopup = true;
        }
      });

      // 检查 popup--bar 类型弹窗是否展开 - 更新检测方式
      const barPopups = document.querySelectorAll('[data-popup-bar-holder]');
      let hasExpandedBarPopup = false;

      barPopups.forEach(popup => {
        // 检查是否有 popup--expanded 类
        if (popup.classList.contains('popup--expanded')) {
          hasExpandedBarPopup = true;
        }
        // 同时保留原有的 aria-expanded 检测作为备选
        const toggle = popup.querySelector('[data-popup-bar-toggle]');
        if (toggle && toggle.getAttribute('aria-expanded') === 'true') {
          hasExpandedBarPopup = true;
        }
      });

      // 检查 cookie 弹窗
      const cookiePopups = document.querySelectorAll('[data-tracking-consent-inner]');
      let hasVisibleCookiePopup = false;

      cookiePopups.forEach(popup => {
        const computedStyle = getComputedStyle(popup);
        if (computedStyle.display !== 'none' &&
          computedStyle.visibility !== 'hidden' &&
          popup.offsetParent !== null) {
          hasVisibleCookiePopup = true;
        }
      });

      return hasVisibleLargePopup || hasExpandedBarPopup || hasVisibleCookiePopup;
    }

    // Toggle chat elements based on popup state
    function toggleChatOnPopup() {
      const shouldHide = isPopupOpen();

      if (shouldHide) {
        hideChatElements();
      } else {
        showChatElements();
      }
    }

    // Listen for popup-related clicks
    document.addEventListener('click', function (e) {
      // Check for popup bar toggle buttons
      if (e.target.closest('[data-popup-bar-toggle]')) {
        setTimeout(toggleChatOnPopup, 100);
      }

      // Check for popup close buttons
      if (e.target.closest('[data-popup-close]')) {
        setTimeout(() => {
          toggleChatOnPopup();
          // 确保聊天按钮重新显示
          setTimeout(showChatElements, 500);
        }, 300);
      }

      // Check for popup underlay clicks
      if (e.target.closest('[data-popup-underlay]')) {
        setTimeout(() => {
          toggleChatOnPopup();
          // 确保聊天按钮重新显示
          setTimeout(showChatElements, 500);
        }, 300);
      }

      // Check for cookie consent buttons
      if (e.target.closest('[data-confirm-cookies]')) {
        setTimeout(() => {
          toggleChatOnPopup();
          setTimeout(showChatElements, 500);
        }, 300);
      }
    });

    // Listen for ESC key to close popups
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') {
        setTimeout(() => {
          toggleChatOnPopup();
          setTimeout(showChatElements, 500);
        }, 300);
      }
    });

    // Listen for DOM changes to detect popup state changes
    const observer = new MutationObserver(function (mutations) {
      mutations.forEach(function (mutation) {
        // Listen for class and style changes
        if (
          mutation.type === 'attributes' &&
          (mutation.attributeName === 'class' ||
            mutation.attributeName === 'style' ||
            mutation.attributeName === 'aria-expanded')
        ) {
          if (
            mutation.target.hasAttribute('data-large-promo-inner') ||
            mutation.target.hasAttribute('data-popup-bar-holder') ||
            mutation.target.hasAttribute('data-tracking-consent-inner') ||
            mutation.target.hasAttribute('data-popup-bar-toggle') ||
            mutation.target.classList.contains('popup') ||
            mutation.target.classList.contains('popup--bar') || // 添加对 popup--bar 的监听
            mutation.target.id?.startsWith('popup--')
          ) {
            setTimeout(toggleChatOnPopup, 100);
          }
        }

        // Listen for newly added popup elements
        if (mutation.addedNodes && mutation.addedNodes.length > 0) {
          for (let i = 0; i < mutation.addedNodes.length; i++) {
            const node = mutation.addedNodes[i];
            if (
              node.nodeType === 1 && // Element node
              (node.hasAttribute?.('data-large-promo') ||
                node.hasAttribute?.('data-popup-bar') ||
                node.hasAttribute?.('data-cookie') ||
                node.classList?.contains('popup') ||
                node.classList?.contains('popup--bar') || // 添加 popup--bar 检测
                node.querySelector?.('[data-large-promo-inner]') ||
                node.querySelector?.('[data-popup-bar-holder]') ||
                node.querySelector?.('[data-tracking-consent-inner]'))
            ) {
              setTimeout(toggleChatOnPopup, 200);
            }
          }
        }

        // Listen for removed popup elements
        if (mutation.removedNodes && mutation.removedNodes.length > 0) {
          for (let i = 0; i < mutation.removedNodes.length; i++) {
            const node = mutation.removedNodes[i];
            if (
              node.nodeType === 1 && // Element node
              (node.hasAttribute?.('data-large-promo') ||
                node.hasAttribute?.('data-popup-bar') ||
                node.hasAttribute?.('data-cookie') ||
                node.classList?.contains('popup') ||
                node.classList?.contains('popup--bar')) // 添加 popup--bar 检测
            ) {
              setTimeout(toggleChatOnPopup, 300);
            }
          }
        }
      });
    });

    // Start observing
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'style', 'aria-expanded'],
    });

    // Also observe specific popup elements if they exist
    const popupElements = document.querySelectorAll('[data-large-promo], [data-popup-bar], [data-cookie], [data-large-promo-inner], [data-popup-bar-holder], [data-tracking-consent-inner], .popup--bar');
    popupElements.forEach(element => {
      observer.observe(element, {
        attributes: true,
        attributeFilter: ['class', 'style', 'aria-expanded'],
      });
    });

    // Initial check when page loads
    setTimeout(() => {
      if (!isPopupOpen()) {
        showChatElements();
      }
    }, 1000);

    console.log('✅ Shopify Chat control for Popups enabled');
  }
}

// 修改初始化函数，添加popup监听
document.addEventListener('DOMContentLoaded', function () {
  // Delay execution to ensure Shopify Chat and popup elements have loaded
  setTimeout(() => {
    hideShopifyChatOnCartOpen();
    hideShopifyChatOnQuickAdd();
    hideShopifyChatOnPopup(); // 添加popup监听
  }, 2000);
});

// // Initialize after page loads
// document.addEventListener('DOMContentLoaded', function () {
//   // Delay execution to ensure Shopify Chat has loaded
//   setTimeout(hideShopifyChatOnCartOpen, 2000);
// });

function getProductObject() {
  const productJson = document.getElementById('ProductJson');

  if (productJson) {
    try {
      return JSON.parse(productJson.textContent || productJson.innerText);
    } catch (e) {
      console.error('Product JSON 解析失败', e);
    }
  }
  return null;
}
function getProductMetaObject() {
  const variantsMetaJson = document.getElementById('ProductVariantsMetaJson');
  if (variantsMetaJson) {
    try {
      return JSON.parse(variantsMetaJson.textContent || variantsMetaJson.innerText);
    } catch (e) {
      console.error('Product Variants Meta JSON 解析失败', e);
    }
  }
  return null;
}

function getProductIconTextConfigObject() {
  const iconTextConfigJson = document.getElementById('ProductIconTextConfigJson');
  if (iconTextConfigJson) {
    try {
      return JSON.parse(iconTextConfigJson.textContent || iconTextConfigJson.innerText);
    } catch (e) {
      console.error('Product Icon Text Config JSON 解析失败', e);
    }
  }
  return null;
}

function sanitizeProductIconTextHTML(text) {
  if (typeof text !== 'string') return '';

  const escaped = text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

  return escaped.replace(/&lt;br\s*\/?&gt;/gi, '<br>');
}

// ========== 监听变体切换事件，执行自定义逻辑 ========== //
document.addEventListener('DOMContentLoaded', function () {
  console.log('custom.js loaded');

  let updateProductSwitchToCarton = (e) => {
    const productMeta = getProductMetaObject();
    const mainProduct = getProductObject();
    
    // Try to find the link scoped to the event target (product container)
    let container = document;
    if (e.target && e.target instanceof Element) {
      const sectionElement = e.target.closest('[data-section-id]') || e.target.closest('.product-single__details');
      if (sectionElement) {
        container = sectionElement;
      }
    }
    
    const productSwitchToCartonLinks = container.querySelectorAll('.product_switch_carton_link');
    
    if (productSwitchToCartonLinks.length > 0 && productMeta) {
      productSwitchToCartonLinks.forEach(productSwitchToCartonLink => {
        const linkContainer = productSwitchToCartonLink.tagName === 'A' ? productSwitchToCartonLink : productSwitchToCartonLink.closest('a');
        
        productMeta.forEach((item) => {
          if (item.id == e.detail.variant.id) {
            const targetProduct = item.metafields?.another_quantity_with_same_size;
            
            if (targetProduct && targetProduct.handle) {
              let targetUrl = `/products/${targetProduct.handle}`;
              
              // Match variant by Size option (case-insensitive)
              if (mainProduct && mainProduct.options && targetProduct.options && targetProduct.variants) {
                  const currentSizeIndex = mainProduct.options.findIndex(opt => opt.toLowerCase().includes('size'));
                  const targetSizeIndex = targetProduct.options.findIndex(opt => opt.toLowerCase().includes('size'));
                  
                  if (currentSizeIndex !== -1 && targetSizeIndex !== -1) {
                    const currentSizeValue = e.detail.variant.options[currentSizeIndex];
                    if (currentSizeValue) {
                        const matchedVariant = targetProduct.variants.find(v => {
                          return v.options[targetSizeIndex] && v.options[targetSizeIndex].toLowerCase() === currentSizeValue.toLowerCase();
                        });
                        if (matchedVariant) {
                          targetUrl += `?variant=${matchedVariant.id}`;
                        }
                    }
                  }
              }

              if (linkContainer) {
                linkContainer.href = targetUrl;
                linkContainer.style.display = 'block';
                
                // Force navigation handling to bypass potential event blockers
                linkContainer.onclick = (event) => {
                  // Allow default behavior if modifier keys are pressed (e.g. Cmd+Click for new tab)
                  if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
                  
                  event.preventDefault();
                  event.stopPropagation();
                  window.location.assign(targetUrl);
                };
              }
            } else {
              if (linkContainer) {
                linkContainer.style.display = 'none';
              }
            }
          }
        })
      });
    }
  }
  let upadteCurrentVariant = (e) => {
    const currentVariantAlt = document.querySelectorAll('.radio-current-selected');
    if (currentVariantAlt) {
      currentVariantAlt.forEach((el, index) => {
        el.innerHTML = e.detail.variant['option' + (index + 1)]
      }
      )
    }
  };
  let updateQuantityPrice = (e) => {
    const quantityPrice = document.querySelectorAll('.radio__button-quantity-price-value');
    const product = getProductObject();
    console.log(e.detail.variant, product);
    quantityPrice.forEach((el) => {
      const customValue = el.getAttribute('value');
      product.variants.forEach((variant) => {
        if (e.detail.variant.option1 == variant.option1 && e.detail.variant.option2 == variant.option2 && customValue == variant.option3) {
          el.innerHTML = '$' + (variant.price / 100).toFixed(2);
        }
      })
    })
    const quantityPerPrice = document.querySelectorAll('.radio__button-quantity-bottom');
    const productMeta = getProductMetaObject();
    quantityPerPrice.forEach((el) => {
      const customValue = el.getAttribute('value');
      console.log('customValue:', customValue);
      product.variants.forEach((variant) => {
        if (e.detail.variant.option1 == variant.option1 && e.detail.variant.option2 == variant.option2 && customValue == variant.option3) {
          console.log("variant", variant)
          console.log('productMeta:', productMeta);
          let test = productMeta.find((item) => {
            return item.title == variant.title
          })
          if (variant.compare_at_price) {
            el.innerHTML = "￠" + (variant.price / test.metafields.quantity_per_carton).toFixed(2) + " per " + '/ ' + ((variant.compare_at_price - variant.price) / variant.compare_at_price * 100).toFixed(2) + "% cheaper in bulk";

          }
          else {
            el.innerHTML = "￠" + (variant.price / test.metafields.quantity_per_carton).toFixed(2) + " per ";

          }

        }
      })
    })

  }
  let updateSpecsTable = (e) => {
    const queryString = window.location.search;
    const urlParams = new URLSearchParams(queryString);
    const variantValue = urlParams.get('variant');
    // console.log('URL variant param:', variantValue);
    const specsTable = document.querySelector('.product__accordion.tech_specs table tbody');
    if (!specsTable) return;
    const productMeta = getProductMetaObject();
    if (!productMeta) return;
    // console.log('productMeta for specs:', productMeta, e.detail.variant.id);
    productMeta.forEach((item) => {
      let currentVariantId;
      if (variantValue && !e) {
        currentVariantId = parseInt(variantValue);
      } else {
        if (e) {
          if (e.detail.variant.id != null)
            currentVariantId = e.detail.variant.id;
        }
      }
      if (!currentVariantId) return;
      if (item.id == currentVariantId) {
        // console.log('Updating specs table with:',item.metafields.table);
        //根据key-value对生成表格行
        let tableRows = '';
        if (item.metafields && Array.isArray(item.metafields.table)) {
          for (const value of item.metafields.table) {
            tableRows += `<tr><td>${value.key}</td><td>${value.value}</td></tr>`;
          }
        }
        // for(const [key,value] of Object.entries(item.metafields.table)){
        //   tableRows += `<tr><td>${value.key}</td><td>${value.value}</td></tr>`;
        // }
        specsTable.innerHTML = tableRows;
      }
    })

  }
  updateSpecsTable(null);
  function updateProductIconText(variant) {
    const iconTextElements = document.querySelectorAll('[data-product-icon-text]');
    if (!iconTextElements.length) return;

    const iconTextConfig = getProductIconTextConfigObject();
    if (!iconTextConfig) return;

    const productFallbackText =
      typeof iconTextConfig.productMetafield === 'string' ? iconTextConfig.productMetafield.trim() : '';
    const defaultText = typeof iconTextConfig.defaultText === 'string' ? iconTextConfig.defaultText : '';

    let variantText = '';
    if (variant && variant.id != null) {
      const productMeta = getProductMetaObject();
      if (Array.isArray(productMeta)) {
        const matchedVariantMeta = productMeta.find((item) => Number(item?.id) === Number(variant.id));
        if (typeof matchedVariantMeta?.metafields?.product_icon_text_sku === 'string') {
          variantText = matchedVariantMeta.metafields.product_icon_text_sku.trim();
        }
      }
    }

    const resolvedText = variantText || productFallbackText || defaultText;
    if (!resolvedText) return;

    iconTextElements.forEach((element) => {
      element.innerHTML = sanitizeProductIconTextHTML(resolvedText);
    });
  }

  function updateSubscriptionPrices(variant) {
    if (!variant) return;

    const currency = window.Shopify?.currency?.active || 'USD';

    const format = (price) => {
      try {
        return formatMoney(price, currency);
      } catch (e) {
        console.warn('formatMoney function not available, using fallback.');
        const value = (price / 100).toFixed(2);
        return `$${value}`;
      }
    };

    const discountedPriceEl = document.querySelector('[data-subscription-discounted-price]');
    if (discountedPriceEl) {
      const discountedPrice = variant.price * 0.85;
      discountedPriceEl.innerHTML = format(discountedPrice);
    }

    const originalPriceEl = document.querySelector('[data-subscription-original-price]');
    if (originalPriceEl) {
      originalPriceEl.innerHTML = format(variant.price);
    }

    const oneTimePriceEl = document.querySelector('[data-one-time-price]');
    if (oneTimePriceEl) {
      oneTimePriceEl.innerHTML = format(variant.price);
    }
  }

  // Initial price update on page load
  const productForPrices = getProductObject();
  if (productForPrices) {
    const initialVariant = productForPrices.selected_or_first_available_variant;
    if (initialVariant) {
      updateSubscriptionPrices(initialVariant);
      updateProductIconText(initialVariant);
    }
  }



  function onVariantChange(e) {
    // debugger
    // console.log('Variant changed:', e.detail.variant.name);
    updateProductSwitchToCarton(e);
    upadteCurrentVariant(e);
    updateQuantityPrice(e);
    updateSpecsTable(e);
    if (e.detail.variant) {
      updateSubscriptionPrices(e.detail.variant);
      updateProductIconText(e.detail.variant);
    }
  }
  document.addEventListener('variant:change', onVariantChange);
  document.addEventListener('theme:variant:change', onVariantChange);

  let cartAddedDrawerTimer;

  function startcartAddedDrawerTimer() {
    clearTimeout(cartAddedDrawerTimer);

    cartAddedDrawerTimer = setTimeout(() => {
      const cartAddedDrawer = document.querySelector('.cart-added-drawer');

      cartAddedDrawer.classList.remove('is-visible');
    }, 3000);
  }
  const cartItemDots = document.querySelectorAll('.menu__item--dots');
  if (cartItemDots) {
    cartItemDots.forEach(dot => {
      if (dot.textContent === '0' || dot.textContent === '') {
        dot.style.display = 'none';
      } else {
        dot.style.display = 'flex';
      }
    });
  }
  // --- Cart Added Drawer Logic ---
  // Use a window flag to ensure this logic runs only once, preventing duplicate event listeners.
  if (!window.cartDrawerInitialized) {
    window.cartDrawerInitialized = true; // Set flag immediately

    const cartAddedDrawer = document.querySelector('.cart-added-drawer');
    if (cartAddedDrawer) {
      const quantityCounter = cartAddedDrawer.querySelector('.cart-added-drawer__quantity');
      const quantityInput = quantityCounter.querySelector('.cart-added-drawer__quantity-field');
      const quantityMinus = quantityCounter.querySelector('.cart-added-drawer__quantity-minus');
      const quantityPlus = quantityCounter.querySelector('.cart-added-drawer__quantity-plus');
      const removeButton = cartAddedDrawer.querySelector('.cart-added-drawer-main_content-bottom-remove');
      const closeButton = cartAddedDrawer.querySelector('.cart-added-drawer-header-close');
      const viewCart = cartAddedDrawer.querySelector('.cart-added-drawer-bottom-view');
      const cartAddedDrawerProgressText = cartAddedDrawer.querySelector('.cart-added-drawer-bottom-progress p');
      const cartAddedDrawerProgressBar = cartAddedDrawer.querySelector('.cart-added-drawer-bottom-progress-bar-fill');
      // const cartItemDots = document.querySelector('.menu__item--dots');

      cartAddedDrawer.onmouseover = () => {
        clearTimeout(cartAddedDrawerTimer);
      }
      cartAddedDrawer.onmouseleave = () => {
        startcartAddedDrawerTimer();
      };

      let debounceTimer;
      const updateCartQuantity = (key, quantity) => {
        clearTimeout(debounceTimer);
        debounceTimer = setTimeout(() => {
          cartAddedDrawer.classList.add('is-loading');

          fetch('/cart/update.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
            body: JSON.stringify({ updates: { [key]: quantity } })
          })
            .then(response => response.json())
            .then(updatedCart => {
              cartAddedDrawer.classList.remove('is-loading');
              if (updatedCart.status === 422) {
                console.error(updatedCart.description);
                const originalItem = updatedCart.items.find(item => item.key === key);
                if (originalItem) {
                  quantityInput.value = originalItem.quantity;
                }
                return;
              }

              const viewCartButton = cartAddedDrawer.querySelector('.cart-added-drawer-bottom-view');
              if (viewCartButton) {
                viewCartButton.textContent = `View cart (${updatedCart.item_count})`;
              }

              const updatedItem = updatedCart.items.find(item => item.key === key);
              if (!updatedItem || quantity === 0) {
                cartAddedDrawer.classList.remove('is-visible');
              } else {
                quantityInput.value = updatedItem.quantity;
                const cartAddedDrawerPrice = cartAddedDrawer.querySelector('.cart-added-drawer-main_content-inner-price');
                updatePriceDisplay(updatedItem, updatedCart.currency, cartAddedDrawerPrice);
              }

              document.dispatchEvent(new CustomEvent('theme:cart:updated', { detail: { cart: updatedCart } }));

              // Find the corresponding item in the main cart and dispatch the event on it
              if (updatedItem) {
                const mainCartItem = document.querySelector(`.cart__item[data-id="${updatedItem.key}"]`);
                if (mainCartItem) {
                  console.log('Found main cart item, dispatching event:', mainCartItem);
                  mainCartItem.dispatchEvent(new CustomEvent('theme:cart:update', {
                    bubbles: true,
                    detail: {
                      id: updatedItem.key,
                      quantity: updatedItem.quantity
                    }
                  }));
                } else {
                  // If item not found (e.g. it was removed), refresh the whole cart as a fallback
                  if (window.theme && window.theme.cart && typeof window.theme.cart.getCart === 'function') {
                    window.theme.cart.getCart();
                  }
                }
              }

              console.log('Cart updated successfully:', quantityInput);
              if (updatedCart.items_subtotal_price >= 4900) {
                cartAddedDrawerProgressText.textContent = `FREE shipping will be applied at checkout`;
                cartAddedDrawerProgressBar.style.width = '100%';
              }
              else {
                cartAddedDrawerProgressText.textContent = `Spend $${(4900 - updatedCart.items_subtotal_price) / 100} more for FREE shipping.`;
                const progressPercentage = Math.min((updatedCart.items_subtotal_price / 4900) * 100, 100);
                cartAddedDrawerProgressBar.style.width = `${progressPercentage}%`;

              }
              document.dispatchEvent(new CustomEvent('theme:cart:refresh', { bubbles: true }));
              cartItemDots.textContent = updatedCart.item_count;
              if (updatedCart.item_count === 0) {
                cartItemDots.style.display = 'none';
              }
              else {
                cartItemDots.style.display = 'flex';
              }

            })
            .catch(e => {
              console.error('Error updating cart:', e);
              cartAddedDrawer.classList.remove('is-loading');
            });
        }, 500);
      };

      // --- Event Delegation for Quantity Buttons ---
      const handleQuantityClick = (event) => {

        // startcartAddedDrawerTimer()
        const button = event.target.closest('[data-quantity-button]');
        if (!button) return;

        const key = cartAddedDrawer.dataset.currentKey;
        if (!key) return;

        let newQuantity = parseInt(quantityInput.value);

        // if (button.matches('.cart-added-drawer__quantity-plus')) {
        //     newQuantity++;

        // } else if (button.matches('.cart-added-drawer__quantity-minus')) {
        //     newQuantity--;
        // }

        quantityInput.value = newQuantity;
        updateCartQuantity(key, newQuantity);
      };

      // --- Define other handlers ---
      const handleQuantityInputChange = () => {
        const key = cartAddedDrawer.dataset.currentKey;
        if (!key) return;
        const newQuantity = parseInt(quantityInput.value);
        if (newQuantity >= 0) {
          updateCartQuantity(key, newQuantity);
        }
      };

      const handleRemoveClick = () => {
        const key = cartAddedDrawer.dataset.currentKey;
        if (!key) return;
        updateCartQuantity(key, 0);
        // cartAddedDrawer.classList.remove('is-visible');
      };

      const handleCloseClick = () => cartAddedDrawer.classList.remove('is-visible');
      const handleViewCartClick = () => { window.location.href = '/cart'; };

      // --- Attach Event Listeners ---
      // Use a flag to avoid re-attaching the delegated listener
      if (!quantityCounter.dataset.listenerAttached) {
        quantityCounter.addEventListener('click', handleQuantityClick);
        quantityCounter.dataset.listenerAttached = 'true';
      }

      quantityInput.removeEventListener('change', handleQuantityInputChange);
      quantityInput.addEventListener('change', handleQuantityInputChange);

      removeButton.removeEventListener('click', handleRemoveClick);
      removeButton.addEventListener('click', handleRemoveClick);

      if (closeButton) {
        closeButton.removeEventListener('click', handleCloseClick);
        closeButton.addEventListener('click', handleCloseClick);
      }
      if (viewCart) {
        viewCart.removeEventListener('click', handleViewCartClick);
        viewCart.addEventListener('click', handleViewCartClick);
      }
    }
  }

  /**
   * Formats money from cents to a localized currency string.
   */
  function formatMoney(cents, currency) {
    const value = (cents / 100);
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
        style: 'currency',
        currency: currency,
        minimumFractionDigits: 2
      }).format(value);
    } catch (e) {
      console.warn(`Could not format currency ${currency}. Falling back to basic format.`, e);
      return `$${value.toFixed(2)}`;
    }
  }

  /**
   * Updates the price display area in the drawer.
   */
  function updatePriceDisplay(item, currency, priceContainer) {
    priceContainer.innerHTML = '';
    const finalPrice = document.createElement('div');
    finalPrice.textContent = formatMoney(item.final_line_price, currency);
    priceContainer.appendChild(finalPrice);

    if (item.original_line_price > item.final_line_price) {
      const originalPrice = document.createElement('s');
      originalPrice.textContent = formatMoney(item.original_line_price, currency);
      priceContainer.appendChild(originalPrice);
    }

    if (item.line_level_discount_allocations && item.line_level_discount_allocations.length > 0) {
      item.line_level_discount_allocations.forEach(discountAllocation => {
        const discountElement = document.createElement('div');
        discountElement.className = 'cart-added-drawer-product_discount';
        discountElement.style.color = 'var(--color-accent)';
        discountElement.style.fontSize = '0.9em';
        discountElement.style.marginTop = '4px';
        discountElement.textContent = discountAllocation.discount_application.title;
        priceContainer.appendChild(discountElement);
      });
    }
  }

  function normalizeSku(value) {
    return (value ?? '').toString().trim().toLowerCase();
  }

  const onlySkuFreeShippingLimitOverrides = {
    'EL-PLATB-13G-100-(2)*1-sm-hwc':59,
    'EL-PLATB-13G-100-(2)*1-hwc':59,
  };
  const onlySkuFreeShippingLimitOverridesBySku = Object.fromEntries(
    Object.entries(onlySkuFreeShippingLimitOverrides).map(([sku, limit]) => [normalizeSku(sku), limit])
  );

  function getOnlySkuInCart(cart) {
    if (!cart || !Array.isArray(cart.items) || cart.items.length === 0) return null;

    const firstSku = normalizeSku(cart.items[0]?.sku);
    if (!firstSku) return null;

    for (const item of cart.items) {
      if (normalizeSku(item?.sku) !== firstSku) return null;
    }

    return firstSku;
  }

  function getDefaultFreeShippingLimitFromDom() {
    const el = document.querySelector('[data-cart-message][data-limit]');
    if (!el) return null;

    const value = Number(el.getAttribute('data-limit'));
    if (!Number.isFinite(value)) return null;

    return value;
  }

  function getDefaultFreeShippingLimitFromCartDrawerTemplate() {
    const template = document.querySelector('[data-cart-drawer-template]');
    if (!template) return null;

    const el = template.content?.querySelector?.('[data-cart-message][data-limit]');
    if (!el) return null;

    const value = Number(el.getAttribute('data-limit'));
    if (!Number.isFinite(value)) return null;

    return value;
  }

  function setCartDrawerTemplateFreeShippingLimit(limit) {
    const template = document.querySelector('[data-cart-drawer-template]');
    if (!template) return;

    const messages = template.content?.querySelectorAll?.('[data-cart-message][data-limit]');
    if (!messages || !messages.length) return;

    messages.forEach((message) => {
      message.setAttribute('data-limit', String(limit));
    });
  }

  const defaultFreeShippingLimit = (() => {
    const fromDom = getDefaultFreeShippingLimitFromDom();
    if (fromDom !== null) return fromDom;

    const fromTemplate = getDefaultFreeShippingLimitFromCartDrawerTemplate();
    if (fromTemplate !== null) return fromTemplate;

    return null;
  })();

  function formatCartMessageMoney(cents) {
    const formatMoneyFn = window.themeVendor?.themeCurrency?.formatMoney;
    const format = window.theme?.moneyFormat;

    if (typeof formatMoneyFn !== 'function' || typeof format !== 'string') {
      return `$${(Number(cents || 0) / 100).toFixed(2)}`;
    }

    let output = formatMoneyFn(cents, format);
    if (typeof output === 'string' && (output.endsWith('.00') || output.endsWith(',00'))) {
      output = output.slice(0, -3);
    }

    if (window.theme?.settings?.currency_code_enable && window.theme?.current_iso_code) {
      output += ` ${window.theme.current_iso_code}`;
    }

    return output;
  }

  function applyFreeShippingLimitOverride(cart) {
    if (Object.keys(onlySkuFreeShippingLimitOverridesBySku).length === 0) return;

    const onlySku = getOnlySkuInCart(cart);
    const overrideLimit = onlySku ? onlySkuFreeShippingLimitOverridesBySku[onlySku] : undefined;
    const parsedOverrideLimit = Number(overrideLimit);

    const baseLimit = defaultFreeShippingLimit;
    if (baseLimit === null) return;

    const effectiveLimit = Number.isFinite(parsedOverrideLimit) ? parsedOverrideLimit : baseLimit;
    if (!Number.isFinite(effectiveLimit)) return;

    const rate = Number(window.Shopify?.currency?.rate ?? 1);
    const currencyRate = Number.isFinite(rate) && rate > 0 ? rate : 1;

    const subtotalCents = Number(cart?.total_price ?? 0);
    const subtotal = Number.isFinite(subtotalCents) ? subtotalCents : 0;

    const limitCentsRaw = effectiveLimit * 100 * currencyRate;
    const limitCents = Number.isFinite(limitCentsRaw) ? limitCentsRaw : 0;

    const hasSubtotal = subtotal !== 0;
    const reachedLimit = limitCents <= 0 ? hasSubtotal : subtotal >= limitCents;
    const leftToSpend = formatCartMessageMoney(limitCents <= 0 ? 0 : limitCents - subtotal);

    const safeLimitCents = limitCents > 0 ? limitCents : 1;
    const percent = hasSubtotal ? (subtotal / safeLimitCents) * 100 : 0;

    // Keep the cart drawer template in sync so the first open uses the overridden threshold.
    setCartDrawerTemplateFreeShippingLimit(effectiveLimit);

    const messages = document.querySelectorAll('[data-cart-message][data-limit]');
    if (!messages.length) return;

    messages.forEach((message) => {
      message.setAttribute('data-limit', String(effectiveLimit));

      const leftToSpendEl = message.querySelector('[data-left-to-spend]');
      if (leftToSpendEl) {
        leftToSpendEl.innerHTML = leftToSpend;
      }

      const cartMessageDefault = message.querySelector('[data-message-default]');
      if (cartMessageDefault) {
        cartMessageDefault.classList.toggle('is-hidden', reachedLimit);
      }

      const hasQualifiedMessage = message.getAttribute('data-cart-message') === 'true' && hasSubtotal;
      message.classList.toggle('is-success', reachedLimit && hasQualifiedMessage);
      message.classList.toggle('is-hidden', !hasSubtotal);

      const progressEls = message.querySelectorAll('[data-cart-message-progress]');
      if (progressEls.length) {
        progressEls.forEach((progressEl, index) => {
          progressEl.classList.toggle('is-hidden', reachedLimit);
          progressEl.style.setProperty('--progress-width', `${percent}%`);
          if (index === 0) {
            progressEl.setAttribute('value', String(percent));
          }
        });
      }
    });

    const cartInstance = window.cart;
    if (cartInstance && typeof cartInstance.updateProgress === 'function') {
      cartInstance.cartFreeLimitShipping = limitCents;
      cartInstance.subtotal = subtotal;
      try {
        cartInstance.updateProgress();
      } catch (e) {}
    }
  }

  if (Object.keys(onlySkuFreeShippingLimitOverridesBySku).length > 0 && (document.querySelector('[data-cart-message][data-limit]') || document.querySelector('[data-cart-drawer-template]'))) {
    fetch('/cart.js')
      .then((response) => response.json())
      .then((cart) => applyFreeShippingLimitOverride(cart))
      .catch(() => {});
  }

  document.addEventListener('theme:cart:loaded', function () {
    if (Object.keys(onlySkuFreeShippingLimitOverridesBySku).length === 0) return;
    fetch('/cart.js')
      .then((response) => response.json())
      .then((cart) => applyFreeShippingLimitOverride(cart))
      .catch(() => {});
  });

  document.addEventListener('theme:cart:open', function () {
    if (Object.keys(onlySkuFreeShippingLimitOverridesBySku).length === 0) return;
    fetch('/cart.js')
      .then((response) => response.json())
      .then((cart) => applyFreeShippingLimitOverride(cart))
      .catch(() => {});
  });

  document.addEventListener('theme:cart:added', function (event) {
    const cartItemDots = document.querySelectorAll('.menu__item--dots');

    console.log('商品已添加，原始数据:', event.detail.cart);
    let addedItemKey = null;
    if (event.detail.cart) {
      addedItemKey = event.detail.cart.key;
    }
    fetch('/cart.js')
      .then(response => response.json())
      .then(cart => {
        console.log('完整购物车数据:', cart);
        applyFreeShippingLimitOverride(cart);
        if (cartItemDots) {
          cartItemDots.forEach(dot => {
            dot.textContent = cart.item_count;
            if (cart.item_count === 0) {
              dot.style.display = 'none';
            } else {
              dot.style.display = 'flex';
            }
          });
        }
        const currentItem = cart.items.find(item => item.key === addedItemKey);
        if (!currentItem) {
          console.error('在购物车中未找到匹配的商品项');
          return;
        }

        console.log('匹配到的商品项:', currentItem);

        const cartAddedDrawer = document.querySelector('.cart-added-drawer');
        if (!cartAddedDrawer) return;

        // --- Populate Drawer with Data ---
        cartAddedDrawer.dataset.currentKey = currentItem.key; // Store the key
        cartAddedDrawer.classList.add('is-visible');
        startcartAddedDrawerTimer()

        const cartAddedDrawerImage = cartAddedDrawer.querySelector('.cart-added-drawer-main_image img');
        const cartAddedDrawerTitle = cartAddedDrawer.querySelector('.cart-added-drawer-product_title');
        const cartAddedDrawerSpec = cartAddedDrawer.querySelector('.cart-added-drawer-product_item_container');
        const cartAddedDrawerPrice = cartAddedDrawer.querySelector('.cart-added-drawer-main_content-inner-price');
        const viewCartButton = cartAddedDrawer.querySelector('.cart-added-drawer-bottom-view');
        const quantityInput = cartAddedDrawer.querySelector('.cart-added-drawer__quantity-field');
        const cartAddedDrawerProgressText = cartAddedDrawer.querySelector('.cart-added-drawer-bottom-progress p');
        const cartAddedDrawerProgressBar = cartAddedDrawer.querySelector('.cart-added-drawer-bottom-progress-bar-fill');



        cartAddedDrawerImage.src = currentItem.image;
        cartAddedDrawerImage.alt = currentItem.product_title;
        cartAddedDrawerTitle.textContent = currentItem.product_title;
        quantityInput.value = currentItem.quantity;

        cartAddedDrawerSpec.innerHTML = '';
        if (currentItem.options_with_values && currentItem.options_with_values.length > 0) {
          currentItem.options_with_values.forEach((option) => {
            const specItem = document.createElement('div');
            specItem.classList.add('cart-added-drawer-product_item');
            specItem.innerHTML = `<span class="cart-added-drawer-product_item_title">${option.name}:</span> <span class="cart-added-drawer-product_item_value">${option.value}</span>`;
            cartAddedDrawerSpec.appendChild(specItem);
          });
        }

        updatePriceDisplay(currentItem, cart.currency, cartAddedDrawerPrice);

        if (viewCartButton) {
          viewCartButton.textContent = `View cart (${cart.item_count})`;
        }
        if (cart.items_subtotal_price >= 4900) {
          cartAddedDrawerProgressText.textContent = `FREE shipping will be applied at checkout`;
          cartAddedDrawerProgressBar.style.width = '100%';
        }
        else {
          cartAddedDrawerProgressText.textContent = `Spend $${(4900 - cart.items_subtotal_price) / 100} more for FREE shipping.`;
          const progressPercentage = Math.min((cart.items_subtotal_price / 4900) * 100, 100);
          cartAddedDrawerProgressBar.style.width = `${progressPercentage}%`;

        }
        document.dispatchEvent(new CustomEvent('theme:cart:refresh', { bubbles: true }));

      })
      .catch(e => {
        console.error('获取购物车数据时出错:', e);
      });


  });
});

// update mosaic item content position
function updateMosaicItemContentPosition() {
  document.querySelectorAll('.mosaic__item__content').forEach(function (mosaicItemContent) {
    let mobilePosition = mosaicItemContent.getAttribute('data-mobile-position');
    let desktopPosition = mosaicItemContent.getAttribute('data-desktop-position');
    if (window.innerWidth > 767) {
      mosaicItemContent.classList.remove(mobilePosition);
      mosaicItemContent.classList.add(desktopPosition);
    } else {
      mosaicItemContent.classList.remove(desktopPosition);
      mosaicItemContent.classList.add(mobilePosition);
    }
  });
}

window.addEventListener('resize', updateMosaicItemContentPosition);

// 页面加载时立即执行一次
updateMosaicItemContentPosition();

/**
 * External Links Handler Class
 * Automatically adds rel="nofollow" attribute to external links in blog articles
 */
class ExternalLinksHandler {
  constructor() {
    // Only execute on article pages
    if (!document.body.classList.contains('template-article')) return;

    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        this.init();
      });
    } else {
      this.init();
    }
  }

  init() {
    // Find the article content container
    const articleContent = document.querySelector('.article__content.rte');

    // Exit if container doesn't exist
    if (!articleContent) return;

    // Get all links within the article content
    const links = articleContent.querySelectorAll('a[href]');

    // Process each link
    links.forEach(link => {
      if (this.isExternalLink(link)) {
        this.addNoFollowAttribute(link);
        // Optional: Add target="_blank" to open in new tab
        link.setAttribute('target', '_blank');
      }
    });
  }

  /**
   * Check if a link is external
   * @param {HTMLAnchorElement} link - The link element to check
   * @returns {boolean} - True if the link is external
   */
  isExternalLink(link) {
    try {
      const currentDomain = window.location.hostname;
      const linkUrl = new URL(link.href, window.location.href);
      const linkHostname = linkUrl.hostname;

      // Return true if the hostname is different and not empty
      return linkHostname !== currentDomain && linkHostname !== '';
    } catch (e) {
      // If URL parsing fails, treat as internal link
      console.warn('Failed to parse URL:', link.href, e);
      return false;
    }
  }

  /**
   * Add rel="nofollow" attribute to a link
   * @param {HTMLAnchorElement} link - The link element to modify
   */
  addNoFollowAttribute(link) {
    // 获取现有的 rel 属性值并处理
    let relValues = link.getAttribute('rel') ? link.getAttribute('rel').split(' ').filter(val => val) : [];

    // 移除 noopener（如果存在）
    relValues = relValues.filter(val => val !== 'noopener');

    // 添加 nofollow（如果不存在）
    if (!relValues.includes('nofollow')) {
      relValues.push('nofollow');
    }

    // 设置新的 rel 属性
    link.setAttribute('rel', relValues.join(' '));
  }
}

// Initialize the External Links Handler
new ExternalLinksHandler();


/* Fix for Quick View Popup Image Compression */
(function () {
  // Inject CSS safety net
  const style = document.createElement('style');
  style.innerHTML = `
    .popup-quick-view .flickity-viewport {
      min-height: 300px; /* Increased default height */
      transition: height 0.2s ease-out;
    }
    /* Ensure images don't look broken while loading */
    .popup-quick-view .flickity-viewport img {
      max-height: 100%;
      object-fit: contain;
    }
  `;
  document.head.appendChild(style);

  // Helper to get Flickity instance
  function getFlickityInstance(element) {
    if (!element) return null;

    // Try global Flickity
    if (window.Flickity && Flickity.data) {
      const instance = Flickity.data(element);
      if (instance) return instance;
    }

    // Try jQuery data
    if (window.jQuery) {
      const $element = window.jQuery(element);
      if ($element.length && $element.data('flickity')) {
        return $element.data('flickity');
      }
    }

    return null;
  }

  // Function to resize Flickity sliders
  function resizeQuickViewSliders() {
    // Target the specific slider in the quick view
    const sliders = document.querySelectorAll('.pswp--open [data-product-single-media-slider], .popup-quick-view [data-product-single-media-slider]');

    if (sliders.length === 0) return;

    sliders.forEach(slider => {
      const flkty = getFlickityInstance(slider);

      if (flkty) {
        // Force resize immediately
        flkty.resize();

        // Also ensure all images are loaded and resize again
        const images = slider.querySelectorAll('img');
        images.forEach(img => {
          // If image is not complete, add listener
          if (!img.complete) {
            img.addEventListener('load', () => flkty.resize());
          }
          // If image is complete but naturalHeight is 0 (broken or not fully rendered), try to reload or wait
          else if (img.naturalHeight === 0) {
            img.addEventListener('load', () => flkty.resize());
          }
        });
      } else {
        // If instance not found, trigger global resize as fallback
        window.dispatchEvent(new Event('resize'));
      }
    });
  }

  // High-frequency polling function
  function startPolling() {
    let count = 0;
    // Poll every 50ms for the first 2 seconds (40 checks)
    // Then every 200ms for another 3 seconds
    const interval = setInterval(() => {
      resizeQuickViewSliders();
      count++;

      if (count > 40) {
        clearInterval(interval);
        // Switch to slower polling
        pollSlowly();
      }
    }, 50);
  }

  function pollSlowly() {
    let count = 0;
    const interval = setInterval(() => {
      resizeQuickViewSliders();
      count++;
      if (count > 15) clearInterval(interval); // Run for another 3 seconds
    }, 200);
  }

  // Observer to detect when Quick View opens
  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      if (mutation.attributeName === 'class') {
        // The theme adds 'js-quick-view-visible' to body
        if (document.body.classList.contains('js-quick-view-visible')) {
          startPolling();
        }
      }
    });
  });

  // Start observing the body
  observer.observe(document.body, {
    attributes: true
  });
})();

function truncateCurrencyCents(value) {
  if (!Number.isFinite(value) || value <= 0) return 0;
  return Math.floor(value + 1e-6);
}

function getDiscountedPriceCents(basePriceCents, discountPercent) {
  const safeBasePriceCents = Number.isFinite(Number(basePriceCents))
    ? Math.max(0, Math.round(Number(basePriceCents)))
    : 0;
  const safeDiscountPercent = Number.isFinite(Number(discountPercent))
    ? Math.max(0, Number(discountPercent))
    : 0;

  const discountCents = Math.min(
    safeBasePriceCents,
    truncateCurrencyCents((safeBasePriceCents * safeDiscountPercent) / 100)
  );

  return Math.max(0, safeBasePriceCents - discountCents);
}

function getUnitPriceCents(totalPriceCents, quantityPerCarton) {
  const safeTotalPriceCents = Number.isFinite(Number(totalPriceCents))
    ? Math.max(0, Math.round(Number(totalPriceCents)))
    : 0;
  const safeQuantityPerCarton = Number(quantityPerCarton);

  if (!Number.isFinite(safeQuantityPerCarton) || safeQuantityPerCarton <= 0) return null;

  return truncateCurrencyCents(safeTotalPriceCents / safeQuantityPerCarton);
}

/* Quantity Discount Logic */
class QuantityDiscountManager {
  constructor() {
    // Restrict scope to .section-product-box as requested
    this.container = document.querySelector('.section-product-box');
    
    // If no product box container is found, stop initialization
    if (!this.container) return;

    this.priceElement = this.container.querySelector('[data-product-price]');
    this.quantityInput = this.container.querySelector('input[name="quantity"]');
    this.variantInput = this.container.querySelector('input[name="id"], select[name="id"]');
    this.productJsonElement = this.container.querySelector('[data-product-json]');
    
    // Attempt to find product JSON
    if (this.productJsonElement) {
      try {
        this.product = JSON.parse(this.productJsonElement.innerHTML);
      } catch(e) { console.error('Error parsing product JSON', e); }
    }

    // Initialize only if required elements exist
    if (!this.priceElement || !this.quantityInput || !this.product) return;
    
    this.init();
  }

  init() {
    this.moneyFormat = (window.theme && window.theme.moneyFormat) || "${{amount}}";
    this.basePrice = 0;

    // Determine initial base price
    this.updateBasePrice();
    // Apply discount immediately in case initial quantity is >= 2 (e.g. browser cached value)
    this.applyDiscount();

    // Event Listeners
    this.quantityInput.addEventListener('input', () => this.applyDiscount());
    this.quantityInput.addEventListener('change', () => this.applyDiscount());

    // Listen for theme variant changes to update base price
    document.addEventListener('theme:variant:change', (e) => {
      // If we need to filter events specifically for this product, we would do it here.
      // Since we are singleton per product-box for now, and theme:variant:change typically bubbles from container
      // we can check if the event target is within our container or check detail.
      
      // Update: theme:variant:change usually bubbles from the product container.
      // If we want to be strict, we could check e.target.closest('.section-product-box') === this.container
      
      if (e.detail.variant) {
        this.basePrice = e.detail.variant.price;
        this.applyDiscount();
      }
    });

    // Handle quantity buttons (plus/minus) which might not trigger standard input events
    this.container.querySelectorAll('.quantity__button').forEach(btn => {
      btn.addEventListener('click', () => {
        // Short delay to allow value to update
        setTimeout(() => this.applyDiscount(), 50);
      });
    });
  }

  updateBasePrice() {
    // 1. Try to find currently selected variant ID from existing input/select
    let currentId = null;
    if (this.variantInput) {
       currentId = parseInt(this.variantInput.value);
    } 
    
    // 2. If valid ID, look it up in product data
    if (currentId && this.product.variants) {
       const variant = this.product.variants.find(v => v.id === currentId);
       if (variant) {
         this.basePrice = variant.price;
         return;
       }
    }

    // 3. Fallback: Use the first available variant or first variant
    // This is a reasonable default for initial load if no specific variant is indicated
    if (this.product.variants && this.product.variants.length > 0) {
      // Logic: theme uses top variant usually
      const firstAvailable = this.product.variants.find(v => v.available) || this.product.variants[0];
      this.basePrice = firstAvailable.price;
    }
  }

  applyDiscount() {
    if (!this.basePrice) return;

    let price = this.basePrice;
    const qty = parseInt(this.quantityInput.value) || 1;

    // Logic: If quantity >= 2, apply 10% discount
    if (qty >= 2) {
      price = getDiscountedPriceCents(price, 10);
    }

    // Update the DOM
    this.priceElement.innerHTML = this.formatMoney(price, this.moneyFormat);
  }

  // Simple money formatter compatible with Shopify formats
  formatMoney(cents, format) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    let value = '';
    const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    
    function formatWithDelimiters(number, precision, thousands, decimal) {
      precision = precision !== undefined ? precision : 2;
      thousands = thousands !== undefined ? thousands : ',';
      decimal = decimal !== undefined ? decimal : '.';
  
      if (isNaN(number) || number == null) {
        return 0;
      }
  
      number = (number / 100.0).toFixed(precision);
  
      var parts = number.split('.');
      var dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      var cents_part = parts[1] ? (decimal + parts[1]) : '';
  
      return dollars + cents_part;
    }
  
    const match = format.match(placeholderRegex);
    if (!match) return '$' + (cents/100).toFixed(2);

    switch (match[1]) {
      case 'amount':
        value = formatWithDelimiters(cents, 2);
        break;
      case 'amount_no_decimals':
        value = formatWithDelimiters(cents, 0);
        break;
      case 'amount_with_comma_separator':
        value = formatWithDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = formatWithDelimiters(cents, 0, '.', ',');
        break;
      default:
        value = formatWithDelimiters(cents, 2);
    }
  
    return format.replace(placeholderRegex, value);
  }
}

/* Tiered Price Table (Web) Logic */
class TieredPriceTableWebManager {
  constructor() {
    this.mutationObserver = null;
    this.syncQueued = false;
    this.lastDataKey = null;
    this.currentVariant = null;
    this.enabled = false;
    this.priceKeyBySection = new WeakMap();

    this.init = this.init.bind(this);
    this.queueSync = this.queueSync.bind(this);
    this.sync = this.sync.bind(this);
    this.handleVariantChange = this.handleVariantChange.bind(this);
    this.handleQuantityInput = this.handleQuantityInput.bind(this);
    this.handleQuantityButtonClick = this.handleQuantityButtonClick.bind(this);

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        setTimeout(this.init, 200);
      });
    } else {
      setTimeout(this.init, 200);
    }

    // Some apps update volume discount info after variant switch
    document.addEventListener('variant:change', this.handleVariantChange);
    document.addEventListener('theme:variant:change', this.handleVariantChange);

    // Theme editor section reload
    document.addEventListener('shopify:section:load', this.queueSync);
  }

  getTargets() {
    return Array.from(document.querySelectorAll('.tiered_price_table_web'));
  }

  init() {
    const targets = this.getTargets();
    if (targets.length === 0) return;

    if (!this.enabled) {
      this.enabled = true;
      document.addEventListener('input', this.handleQuantityInput, { passive: true });
      document.addEventListener('change', this.handleQuantityInput, { passive: true });
      document.addEventListener('click', this.handleQuantityButtonClick);
    }

    // Start hidden until we confirm the app widget exists + has data
    targets.forEach((el) => el.classList.add('hidden'));

    if (this.mutationObserver) return;

    // Watch for the lb widget being injected/hydrated and for table updates
    this.mutationObserver = new MutationObserver(this.queueSync);
    this.mutationObserver.observe(document.body, {
      childList: true,
      subtree: true
    });

    this.queueSync();
  }

  handleVariantChange(e) {
    if (e?.detail?.variant) {
      const nextVariant = e.detail.variant;
      const nextId = Number(nextVariant.id);
      this.currentVariant = Number.isFinite(nextId) ? { ...nextVariant, id: nextId } : nextVariant;
    }
    this.queueSync();
  }

  handleQuantityInput(e) {
    if (!this.enabled) return;

    const target = e?.target;
    if (!(target instanceof HTMLInputElement)) return;
    if (target.name !== 'quantity') return;

    // Only react to the product quantity selector UI
    if (!target.closest('.selector-wrapper--qty')) return;

    this.queueSync();
  }

  handleQuantityButtonClick(e) {
    if (!this.enabled) return;

    const button = e?.target?.closest?.('[data-quantity-button], [data-quantity-plus], [data-quantity-minus], .quantity__btn');
    if (!button) return;
    if (!button.closest('.selector-wrapper--qty')) return;

    // Wait for the theme's quantity-counter to apply the new value to the input
    setTimeout(this.queueSync, 0);
  }

  getSelectedVariantId() {
    const urlVariantId = new URLSearchParams(window.location.search).get('variant');
    if (urlVariantId && !Number.isNaN(parseInt(urlVariantId, 10))) return parseInt(urlVariantId, 10);

    const idInput = document.querySelector('form[action*="/cart/add"] [name="id"], [name="id"]');
    if (idInput && idInput.value && !Number.isNaN(parseInt(idInput.value, 10))) return parseInt(idInput.value, 10);

    return null;
  }

  getCurrentVariant() {
    if (this.currentVariant && typeof this.currentVariant.price === 'number') return this.currentVariant;

    const product = typeof getProductObject === 'function' ? getProductObject() : null;
    if (!product || !Array.isArray(product.variants) || product.variants.length === 0) return null;

    const selectedVariantId = this.getSelectedVariantId();
    if (selectedVariantId) {
      const matched = product.variants.find((v) => v && v.id === selectedVariantId);
      if (matched) return matched;
    }

    return product.variants.find((v) => v && v.available) || product.variants[0];
  }

  getQuantityPerCartonFromMetafields(variantId) {
    const meta = typeof getProductMetaObject === 'function' ? getProductMetaObject() : null;
    if (!Array.isArray(meta) || !variantId) return null;

    const targetId = Number(variantId);
    const match = meta.find((m) => m && Number(m.id) === targetId);
    const value = Number(match?.metafields?.quantity_per_carton);
    if (!Number.isFinite(value) || value <= 0) return null;

    return value;
  }

  getVariantOptions(variant) {
    if (!variant) return null;

    if (Array.isArray(variant.options) && variant.options.length > 0) {
      return variant.options;
    }

    const options = [];
    if (variant.option1 != null) options.push(variant.option1);
    if (variant.option2 != null) options.push(variant.option2);
    if (variant.option3 != null) options.push(variant.option3);
    return options.length > 0 ? options : null;
  }

  getQuantityPerCartonFromOptions(variant) {
    const options = this.getVariantOptions(variant);
    if (!options || options.length === 0) return null;

    const last = options[options.length - 1];
    if (typeof last !== 'string') return null;

    const text = last.trim();
    if (!text) return null;

    // Examples:
    // - "36/carton"
    // - "36 / carton"
    // - "36 per carton"
    const match = text.match(/(\d+(?:\.\d+)?)\s*(?:\/\s*carton\b|per\s*carton\b|carton\b)/i);
    if (!match) return null;

    const value = Number(match[1]);
    if (!Number.isFinite(value) || value <= 0) return null;

    return value;
  }

  getQuantityPerCarton(variant) {
    const metafieldValue = this.getQuantityPerCartonFromMetafields(variant?.id);
    if (metafieldValue) return metafieldValue;

    return this.getQuantityPerCartonFromOptions(variant);
  }

  getMoneyFormat() {
    if (window.theme?.settings?.currency_code_enable && window.theme?.moneyWithCurrencyFormat) {
      return window.theme.moneyWithCurrencyFormat;
    }
    return window.theme?.moneyFormat || null;
  }

  formatMoney(cents) {
    const moneyFormat = this.getMoneyFormat();

    if (window.themeCurrency?.formatMoney && moneyFormat) {
      return window.themeCurrency.formatMoney(cents, moneyFormat);
    }

    if (window.Shopify?.formatMoney && moneyFormat) {
      return window.Shopify.formatMoney(cents, moneyFormat);
    }

    return `$${(cents / 100).toFixed(2)}`;
  }

  getSourceTable() {
    const root = document.getElementById('lb-volume-discount');
    if (!root) return null;

    const lightDomTable = root.querySelector('table.lb-vd-table, table#lb-vd-table, table');
    if (lightDomTable) return lightDomTable;

    const component = root.querySelector('lb-volume-discount');
    const shadowTable = component?.shadowRoot?.querySelector('table.lb-vd-table, table#lb-vd-table, table');
    return shadowTable || null;
  }

  parseDiscountPercent(discountText) {
    if (!discountText) return 0;
    const match = discountText.match(/(\d+(?:\.\d+)?)\s*%/);
    if (!match) return 0;
    const value = Number(match[1]);
    if (!Number.isFinite(value) || value < 0) return 0;
    return value;
  }

  formatQtyLabel(buyQuantityText) {
    if (!buyQuantityText) return '';

    // Examples:
    // - "Buy 1" -> "1 carton"
    // - "Buy 2 - 10" -> "2-10 cartons"
    // - "Buy 11+" -> "11+ cartons"
    const cleaned = buyQuantityText.replace(/^buy\s+/i, '').trim();
    if (!cleaned) return buyQuantityText.trim();

    const normalized = cleaned.replace(/\s*-\s*/g, '-').replace(/\s*\+\s*/g, '+').trim();

    // If it's a single number
    if (/^\d+$/.test(normalized)) {
      return `${normalized} ${normalized === '1' ? 'carton' : 'cartons'}`;
    }

    // Range or plus
    if (/^\d+-\d+$/.test(normalized) || /^\d+\+$/.test(normalized)) {
      const isSingle = normalized === '1';
      return `${normalized} ${isSingle ? 'carton' : 'cartons'}`;
    }

    return buyQuantityText.trim();
  }

  parseBuyQuantityRange(buyQuantityText) {
    if (!buyQuantityText) return null;

    let normalized = buyQuantityText.trim();
    if (!normalized) return null;

    normalized = normalized.replace(/^buy\s+/i, '').trim();
    normalized = normalized.replace(/[–—]/g, '-');
    normalized = normalized.replace(/\s+to\s+/gi, '-');
    normalized = normalized.replace(/\s*-\s*/g, '-');
    normalized = normalized.replace(/\s*\+\s*/g, '+');

    if (/^\d+$/.test(normalized)) {
      const value = Number(normalized);
      return Number.isFinite(value) && value > 0 ? { minQty: value, maxQty: value } : null;
    }

    const rangeMatch = normalized.match(/^(\d+)-(\d+)$/);
    if (rangeMatch) {
      const minQty = Number(rangeMatch[1]);
      const maxQty = Number(rangeMatch[2]);
      if (!Number.isFinite(minQty) || !Number.isFinite(maxQty) || minQty <= 0 || maxQty < minQty) return null;
      return { minQty, maxQty };
    }

    const plusMatch = normalized.match(/^(\d+)\+$/);
    if (plusMatch) {
      const minQty = Number(plusMatch[1]);
      if (!Number.isFinite(minQty) || minQty <= 0) return null;
      return { minQty, maxQty: null };
    }

    return null;
  }

  extractTiers(table) {
    if (!table) return null;

    const rows = Array.from(table.querySelectorAll('tbody tr'))
      .map((tr) => {
        const cells = Array.from(tr.querySelectorAll('th, td')).map((cell) => (cell.textContent || '').trim());
        const buyQuantity = cells[0] || '';
        const discount = cells[1] || '';
        const range = this.parseBuyQuantityRange(buyQuantity);
        return {
          qtyText: this.formatQtyLabel(buyQuantity),
          discountPercent: this.parseDiscountPercent(discount),
          minQty: range?.minQty ?? null,
          maxQty: range?.maxQty ?? null
        };
      })
      .filter((row) => row.qtyText);

    if (rows.length === 0) return null;

    return rows;
  }

  getProductSectionsFromTargets(targets) {
    const sections = new Set();

    targets.forEach((target) => {
      const section =
        target.closest('[data-section-type="product"]') ||
        target.closest('[data-section-id]') ||
        document;
      sections.add(section);
    });

    return Array.from(sections);
  }

  setTwoOrMoreWrapperVisibility(section, visible) {
    if (!section) return;
    const wrapper = section.querySelector('.product__two-or-more-wrapper');
    if (!wrapper) return;
    wrapper.classList.toggle('hidden', !visible);
  }

  getSelectedQuantity(section) {
    const input = section.querySelector('.selector-wrapper--qty input[name="quantity"]');
    if (!input) return 1;

    const value = parseInt(input.value, 10);
    if (!Number.isFinite(value) || value <= 0) return 1;

    return value;
  }

  getDiscountPercentForQuantity(tiers, quantity) {
    if (!Array.isArray(tiers) || !Number.isFinite(quantity) || quantity <= 0) return 0;

    let matchedTier = null;

    tiers.forEach((tier) => {
      if (tier?.minQty == null || !Number.isFinite(Number(tier.minQty))) return;

      const minQty = Number(tier.minQty);
      const maxQty = tier.maxQty == null ? null : Number(tier.maxQty);

      if (quantity < minQty) return;
      if (maxQty != null && Number.isFinite(maxQty) && quantity > maxQty) return;

      // Prefer the highest minQty that still matches (most specific tier)
      if (!matchedTier || minQty >= Number(matchedTier.minQty || 0)) {
        matchedTier = tier;
      }
    });

    const discountPercent = Number(matchedTier?.discountPercent || 0);
    if (!Number.isFinite(discountPercent) || discountPercent < 0) return 0;

    return discountPercent;
  }

  formatMoneyWithShopFormat(cents) {
    const shopFormat = window.theme?.moneyFormat;
    if (window.Shopify?.formatMoney && shopFormat) {
      return window.Shopify.formatMoney(cents, shopFormat);
    }
    return this.formatMoney(cents);
  }

  applyProductPriceForSection(section, variant, tiers) {
    if (!section || !variant || typeof variant.price !== 'number') return;

    const quantity = this.getSelectedQuantity(section);
    const discountPercent = this.getDiscountPercentForQuantity(tiers, quantity);
    const discountedCents = getDiscountedPriceCents(variant.price, discountPercent);

    const formattedPrice =
      discountedCents === 0 && window.theme?.strings?.free
        ? window.theme.strings.free
        : this.formatMoney(discountedCents);

    const key = JSON.stringify({
      variantId: variant.id,
      basePrice: variant.price,
      quantity,
      discountPercent,
      discountedCents,
      formattedPrice
    });

    if (this.priceKeyBySection.get(section) === key) return;

    const priceElements = section.querySelectorAll('[data-product-price]');
    if (priceElements.length === 0) return;

    // Update "Save" badge amount to match the displayed (tiered) price
    const priceOffAmountElements = section.querySelectorAll('[data-price-off-amount]');
    if (priceOffAmountElements.length) {
      const comparePriceCents =
        typeof variant.compare_at_price === 'number' && variant.compare_at_price > 0
          ? variant.compare_at_price
          : variant.price;
      const savingsCents = Math.max(0, comparePriceCents - discountedCents);

      let offText = '';
      if (window.theme?.settings?.savingBadgeType === 'percentage') {
        if (comparePriceCents > 0) {
          const discountFloat = savingsCents / comparePriceCents;
          const discountInt = Math.floor(discountFloat * 100);
          offText = `${discountInt}%`;
        }
      } else {
        offText = this.formatMoneyWithShopFormat(savingsCents);
      }

      priceOffAmountElements.forEach((el) => {
        el.innerHTML = offText;
      });
    }

    priceElements.forEach((el) => {
      el.innerHTML = formattedPrice;
    });

    this.priceKeyBySection.set(section, key);
  }

  restoreBaseProductPriceForSection(section, variant) {
    if (!section || !variant || typeof variant.price !== 'number') return;

    const formattedPrice =
      variant.price === 0 && window.theme?.strings?.free
        ? window.theme.strings.free
        : this.formatMoney(variant.price);

    const key = JSON.stringify({
      variantId: variant.id,
      basePrice: variant.price,
      quantity: null,
      discountPercent: 0,
      discountedCents: variant.price,
      formattedPrice,
      mode: 'base'
    });

    if (this.priceKeyBySection.get(section) === key) return;

    const priceElements = section.querySelectorAll('[data-product-price]');
    if (priceElements.length === 0) return;

    priceElements.forEach((el) => {
      el.innerHTML = formattedPrice;
    });

    this.priceKeyBySection.set(section, key);
  }

  ensureHeader(table) {
    if (!table) return;
    let thead = table.querySelector('thead');
    if (thead && thead.querySelectorAll('th').length >= 2) return;

    if (!thead) {
      thead = document.createElement('thead');
      table.insertBefore(thead, table.firstChild);
    }

    thead.innerHTML = '';
    const tr = document.createElement('tr');
    const thQty = document.createElement('th');
    thQty.textContent = 'QTY';
    const thPrice = document.createElement('th');
    thPrice.textContent = 'Volume Pricing';
    tr.appendChild(thQty);
    tr.appendChild(thPrice);
    thead.appendChild(tr);
  }

  setQtyCellText(td, qtyText) {
    if (!td) return;

    const text = typeof qtyText === 'string' ? qtyText.trim() : String(qtyText || '').trim();
    if (!text) {
      td.textContent = '';
      return;
    }

    const parts = text.split(/\s+/);
    if (parts.length < 2) {
      const gluedMatch = text.match(/^(\d(?:[\d-]*\d)?\+?)(cartons?)$/i);
      if (gluedMatch) {
        td.textContent = '';
        td.appendChild(document.createTextNode(gluedMatch[1]));
        td.appendChild(document.createElement('br'));
        td.appendChild(document.createTextNode(gluedMatch[2]));
        return;
      }

      td.textContent = text;
      return;
    }

    const unit = parts.pop();
    const numberPart = parts.join(' ');

    td.textContent = '';
    td.appendChild(document.createTextNode(numberPart));
    td.appendChild(document.createElement('br'));
    td.appendChild(document.createTextNode(unit));
  }

  buildRow({ qtyText, priceText, unitText, savingsText }) {
    const tr = document.createElement('tr');

    const tdQty = document.createElement('td');
    tdQty.className = 'product__tiered-price-table-qty';
    this.setQtyCellText(tdQty, qtyText);

    const tdPricing = document.createElement('td');
    const pricing = document.createElement('div');
    pricing.className = 'product__tiered-price-table-pricing';

    const main = document.createElement('div');
    main.className = 'product__tiered-price-table-pricing-main';

    const priceNum = document.createElement('span');
    priceNum.className = 'product__tiered-price-table-pricing-num';
    priceNum.textContent = priceText || '';

    const unit = document.createElement('span');
    unit.className = 'product__tiered-price-table-pricing-unit';
    unit.textContent = unitText || '';

    main.appendChild(priceNum);
    main.appendChild(unit);

    const savings = document.createElement('div');
    savings.className = 'product__tiered-price-table-pricing-savings';
    savings.textContent = savingsText || '';

    pricing.appendChild(main);
    pricing.appendChild(savings);
    tdPricing.appendChild(pricing);

    tr.appendChild(tdQty);
    tr.appendChild(tdPricing);

    return tr;
  }

  render(target, tiers, basePriceCents, quantityPerCarton) {
    const table = target.querySelector('table');
    if (!table) return;

    this.ensureHeader(table);

    let tbody = table.querySelector('tbody');
    if (!tbody) {
      tbody = document.createElement('tbody');
      table.appendChild(tbody);
    }

    tbody.innerHTML = '';

    tiers.forEach((tier) => {
      const discountPercent = typeof tier.discountPercent === 'number' ? tier.discountPercent : 0;
      const discountedCents = getDiscountedPriceCents(basePriceCents, discountPercent);

      let unitText = '';
      if (quantityPerCarton) {
        const eachCents = getUnitPriceCents(discountedCents, quantityPerCarton);
        if (eachCents != null) {
          unitText = `${this.formatMoney(eachCents)}/pc`;
        }
      }

      let savingsText = '';
      if (discountPercent > 0) {
        const percentText = Number.isInteger(discountPercent) ? discountPercent : Number(discountPercent.toFixed(2));
        savingsText = `-${percentText}%off`;
      }

      tbody.appendChild(
        this.buildRow({
          qtyText: tier.qtyText,
          priceText: this.formatMoney(discountedCents),
          unitText,
          savingsText
        })
      );
    });
  }

  queueSync() {
    if (this.syncQueued) return;
    this.syncQueued = true;

    requestAnimationFrame(() => {
      this.syncQueued = false;
      this.sync();
    });
  }

  sync() {
    const targets = this.getTargets();
    if (targets.length === 0) return;

    const sections = this.getProductSectionsFromTargets(targets);

    const variant = this.getCurrentVariant();
    if (!variant || typeof variant.price !== 'number') {
      targets.forEach((el) => el.classList.add('hidden'));
      this.lastDataKey = null;
      sections.forEach((section) => this.setTwoOrMoreWrapperVisibility(section, true));
      return;
    }

    const tiers = this.extractTiers(this.getSourceTable());
    if (!tiers) {
      targets.forEach((el) => el.classList.add('hidden'));
      const hadTieredData = this.lastDataKey != null;
      this.lastDataKey = null;
      if (hadTieredData) {
        sections.forEach((section) => this.restoreBaseProductPriceForSection(section, variant));
      }
      sections.forEach((section) => this.setTwoOrMoreWrapperVisibility(section, true));
      return;
    }

    const quantityPerCarton = this.getQuantityPerCarton(variant);
    const nextKey = JSON.stringify({
      variantId: variant.id,
      basePrice: variant.price,
      quantityPerCarton,
      tiers
    });

    if (nextKey !== this.lastDataKey) {
      targets.forEach((el) => this.render(el, tiers, variant.price, quantityPerCarton));
      this.lastDataKey = nextKey;
    }

    sections.forEach((section) => this.applyProductPriceForSection(section, variant, tiers));

    // When lb-volume-discount has data, only show the tiered table and hide the 2+ cartons message
    sections.forEach((section) => this.setTwoOrMoreWrapperVisibility(section, false));
    targets.forEach((el) => el.classList.remove('hidden'));
  }
}

/* Tiered Price Table (Fac) Logic */
class TieredPriceTableFacManager {
  constructor() {
    this.syncQueued = false;
    this.lastDataKey = null;
    this.currentVariant = null;
    this.enabled = false;
    this.inquireToggleBound = false;

    this.init = this.init.bind(this);
    this.queueSync = this.queueSync.bind(this);
    this.sync = this.sync.bind(this);
    this.handleVariantChange = this.handleVariantChange.bind(this);
    this.handleInquireToggleClick = this.handleInquireToggleClick.bind(this);

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        setTimeout(this.init, 200);
      });
    } else {
      setTimeout(this.init, 200);
    }

    document.addEventListener('variant:change', this.handleVariantChange);
    document.addEventListener('theme:variant:change', this.handleVariantChange);
    document.addEventListener('shopify:section:load', this.queueSync);
  }

  getTargets() {
    return Array.from(document.querySelectorAll('.tiered_price_table_fac_wrapper'));
  }

  prefersReducedMotion() {
    return !!window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  clearWrapperAnimationStyles(wrapper) {
    if (!wrapper) return;
    wrapper.style.height = '';
    wrapper.style.overflow = '';
    wrapper.style.opacity = '';
    wrapper.style.transition = '';
    wrapper.style.willChange = '';
  }

  openWrapper(wrapper) {
    if (!wrapper) return;

    const wasHidden = wrapper.classList.contains('hidden');
    wrapper.dataset.facOpen = 'true';
    wrapper.classList.remove('hidden');

    if (this.prefersReducedMotion()) {
      this.clearWrapperAnimationStyles(wrapper);
      return;
    }

    const animId = Number(wrapper.dataset.facAnimId || 0) + 1;
    wrapper.dataset.facAnimId = String(animId);

    const currentOpacity = parseFloat(window.getComputedStyle(wrapper).opacity);
    const startOpacity = wasHidden ? 0 : Number.isFinite(currentOpacity) ? currentOpacity : 1;
    const startHeight = wasHidden ? 0 : wrapper.getBoundingClientRect().height;

    wrapper.style.overflow = 'hidden';
    wrapper.style.willChange = 'height, opacity';
    wrapper.style.transition = 'none';
    wrapper.style.height = `${startHeight}px`;
    wrapper.style.opacity = String(startOpacity);

    wrapper.getBoundingClientRect();

    const targetHeight = wrapper.scrollHeight;
    wrapper.style.transition =
      'height 280ms cubic-bezier(0.2, 0, 0, 1), opacity 280ms cubic-bezier(0.2, 0, 0, 1)';
    wrapper.style.height = `${targetHeight}px`;
    wrapper.style.opacity = '1';

    const finish = () => {
      if (wrapper.dataset.facAnimId !== String(animId)) return;
      this.clearWrapperAnimationStyles(wrapper);
    };

    const onEnd = (ev) => {
      if (wrapper.dataset.facAnimId !== String(animId)) return;
      if (ev.target !== wrapper) return;
      if (ev.propertyName !== 'height') return;
      wrapper.removeEventListener('transitionend', onEnd);
      finish();
    };

    wrapper.addEventListener('transitionend', onEnd);
    window.setTimeout(() => {
      if (wrapper.dataset.facAnimId !== String(animId)) return;
      wrapper.removeEventListener('transitionend', onEnd);
      finish();
    }, 400);
  }

  closeWrapper(wrapper) {
    if (!wrapper) return;

    wrapper.dataset.facOpen = 'false';

    if (this.prefersReducedMotion()) {
      wrapper.classList.add('hidden');
      this.clearWrapperAnimationStyles(wrapper);
      return;
    }

    const animId = Number(wrapper.dataset.facAnimId || 0) + 1;
    wrapper.dataset.facAnimId = String(animId);

    wrapper.classList.remove('hidden');

    const currentOpacity = parseFloat(window.getComputedStyle(wrapper).opacity);
    const currentHeight = wrapper.getBoundingClientRect().height;
    const startOpacity = Number.isFinite(currentOpacity) ? currentOpacity : 1;

    wrapper.style.overflow = 'hidden';
    wrapper.style.willChange = 'height, opacity';
    wrapper.style.transition = 'none';
    wrapper.style.height = `${currentHeight}px`;
    wrapper.style.opacity = String(startOpacity);

    wrapper.getBoundingClientRect();

    wrapper.style.transition =
      'height 240ms cubic-bezier(0.4, 0, 0.2, 1), opacity 240ms cubic-bezier(0.4, 0, 0.2, 1)';
    wrapper.style.height = '0px';
    wrapper.style.opacity = '0';

    const finish = () => {
      if (wrapper.dataset.facAnimId !== String(animId)) return;
      wrapper.classList.add('hidden');
      this.clearWrapperAnimationStyles(wrapper);
    };

    const onEnd = (ev) => {
      if (wrapper.dataset.facAnimId !== String(animId)) return;
      if (ev.target !== wrapper) return;
      if (ev.propertyName !== 'height') return;
      wrapper.removeEventListener('transitionend', onEnd);
      finish();
    };

    wrapper.addEventListener('transitionend', onEnd);
    window.setTimeout(() => {
      if (wrapper.dataset.facAnimId !== String(animId)) return;
      wrapper.removeEventListener('transitionend', onEnd);
      finish();
    }, 350);
  }

  toggleWrapper(wrapper) {
    if (!wrapper) return;

    const isOpen = wrapper.dataset.facOpen === 'true';
    if (isOpen) {
      this.closeWrapper(wrapper);
    } else {
      this.openWrapper(wrapper);
    }
  }

  init() {
    const targets = this.getTargets();
    if (targets.length === 0) return;

    if (!this.enabled) {
      this.enabled = true;
    }

    if (!this.inquireToggleBound) {
      this.inquireToggleBound = true;
      document.addEventListener('click', this.handleInquireToggleClick, true);
    }

    // Default hidden (even when data exists)
    targets.forEach((el) => {
      el.classList.add('hidden');
      el.dataset.facOpen = 'false';
      this.clearWrapperAnimationStyles(el);
    });

    this.queueSync();
  }

  handleInquireToggleClick(e) {
    const button = e?.target?.closest?.('.product-inquire-wrapper .product-inquire-button');
    if (!button) return;

    const section =
      button.closest('[data-section-type="product"]') ||
      button.closest('[data-section-id]') ||
      button.closest('.product-single') ||
      document;

    const wrapper = section.querySelector('.tiered_price_table_fac_wrapper[data-fac-ready="true"]');
    if (!wrapper) return;

    // Repurpose "Inquire now" to toggle the factory discount table when data exists
    e.preventDefault();
    this.toggleWrapper(wrapper);
  }

  handleVariantChange(e) {
    if (e?.detail?.variant) {
      const nextVariant = e.detail.variant;
      const nextId = Number(nextVariant.id);
      this.currentVariant = Number.isFinite(nextId) ? { ...nextVariant, id: nextId } : nextVariant;
    }
    this.queueSync();
  }

  getSelectedVariantId() {
    const urlVariantId = new URLSearchParams(window.location.search).get('variant');
    if (urlVariantId && !Number.isNaN(parseInt(urlVariantId, 10))) return parseInt(urlVariantId, 10);

    const idInput = document.querySelector('form[action*="/cart/add"] [name="id"], [name="id"]');
    if (idInput && idInput.value && !Number.isNaN(parseInt(idInput.value, 10))) return parseInt(idInput.value, 10);

    return null;
  }

  getCurrentVariant() {
    if (this.currentVariant && typeof this.currentVariant.price === 'number') return this.currentVariant;

    const product = typeof getProductObject === 'function' ? getProductObject() : null;
    if (!product || !Array.isArray(product.variants) || product.variants.length === 0) return null;

    const selectedVariantId = this.getSelectedVariantId();
    if (selectedVariantId) {
      const matched = product.variants.find((v) => v && v.id === selectedVariantId);
      if (matched) return matched;
    }

    return product.variants.find((v) => v && v.available) || product.variants[0];
  }

  getVariantMeta(variantId) {
    const meta = typeof getProductMetaObject === 'function' ? getProductMetaObject() : null;
    if (!Array.isArray(meta) || !variantId) return null;

    const targetId = Number(variantId);
    return meta.find((m) => m && Number(m.id) === targetId) || null;
  }

  normalizeQtyText(text) {
    if (typeof text !== 'string') return '';
    return text
      .trim()
      .replace(/[–—]/g, '-')
      .replace(/\s*-\s*/g, '-')
      .replace(/\s*\+\s*/g, '+')
      .replace(/\s+/g, ' ');
  }

  parseFactoryDiscountData(value) {
    if (!value) return null;

    let data = value;
    if (typeof data === 'string') {
      const trimmed = data.trim();
      if (!trimmed) return null;
      try {
        data = JSON.parse(trimmed);
      } catch (e) {
        return null;
      }
    }

    if (!Array.isArray(data)) return null;

    const rows = data
      .map((item) => {
        if (!item || typeof item !== 'object') return null;

        const qtyText = this.normalizeQtyText(String(item.num || ''));
        if (!qtyText) return null;

        const discountPercent = Number(item.discount);
        const customizationText =
          item.customization == null ? '' : this.normalizeQtyText(String(item.customization));

        return {
          qtyText,
          discountPercent: Number.isFinite(discountPercent) && discountPercent > 0 ? discountPercent : 0,
          customizationText
        };
      })
      .filter(Boolean);

    return rows.length > 0 ? rows : null;
  }

  getVariantOptions(variant) {
    if (!variant) return null;

    if (Array.isArray(variant.options) && variant.options.length > 0) {
      return variant.options;
    }

    const options = [];
    if (variant.option1 != null) options.push(variant.option1);
    if (variant.option2 != null) options.push(variant.option2);
    if (variant.option3 != null) options.push(variant.option3);
    return options.length > 0 ? options : null;
  }

  getQuantityPerCartonFromOptions(variant) {
    const options = this.getVariantOptions(variant);
    if (!options || options.length === 0) return null;

    const last = options[options.length - 1];
    if (typeof last !== 'string') return null;

    const text = last.trim();
    if (!text) return null;

    const match = text.match(/(\d+(?:\.\d+)?)\s*(?:\/\s*carton\b|per\s*carton\b|carton\b)/i);
    if (!match) return null;

    const value = Number(match[1]);
    if (!Number.isFinite(value) || value <= 0) return null;

    return value;
  }

  getQuantityPerCarton(variant) {
    const meta = this.getVariantMeta(variant?.id);
    const metaValue = Number(meta?.metafields?.quantity_per_carton);
    if (Number.isFinite(metaValue) && metaValue > 0) return metaValue;

    return this.getQuantityPerCartonFromOptions(variant);
  }

  getMoneyFormat() {
    if (window.theme?.settings?.currency_code_enable && window.theme?.moneyWithCurrencyFormat) {
      return window.theme.moneyWithCurrencyFormat;
    }
    return window.theme?.moneyFormat || null;
  }

  formatMoney(cents) {
    const moneyFormat = this.getMoneyFormat();

    if (window.themeCurrency?.formatMoney && moneyFormat) {
      return window.themeCurrency.formatMoney(cents, moneyFormat);
    }

    if (window.Shopify?.formatMoney && moneyFormat) {
      return window.Shopify.formatMoney(cents, moneyFormat);
    }

    return `$${(cents / 100).toFixed(2)}`;
  }

  setQtyCellText(td, qtyText) {
    if (!td) return;

    const text = typeof qtyText === 'string' ? qtyText.trim() : String(qtyText || '').trim();
    if (!text) {
      td.textContent = '';
      return;
    }

    const parts = text.split(/\s+/);
    if (parts.length < 2) {
      const gluedMatch = text.match(/^(\d(?:[\d-]*\d)?\+?)(cartons?)$/i);
      if (gluedMatch) {
        td.textContent = '';
        td.appendChild(document.createTextNode(gluedMatch[1]));
        td.appendChild(document.createElement('br'));
        td.appendChild(document.createTextNode(gluedMatch[2]));
        return;
      }

      td.textContent = text;
      return;
    }

    const unit = parts.pop();
    const numberPart = parts.join(' ');

    td.textContent = '';
    td.appendChild(document.createTextNode(numberPart));
    td.appendChild(document.createElement('br'));
    td.appendChild(document.createTextNode(unit));
  }

  buildRow({ qtyText, priceText, unitText, savingsText, customizationText }) {
    const tr = document.createElement('tr');

    const tdQty = document.createElement('td');
    tdQty.className = 'product__tiered-price-table-qty';
    this.setQtyCellText(tdQty, qtyText);

    const tdPricing = document.createElement('td');
    const pricing = document.createElement('div');
    pricing.className = 'product__tiered-price-table-pricing';

    const main = document.createElement('div');
    main.className = 'product__tiered-price-table-pricing-main';

    const priceNum = document.createElement('span');
    priceNum.className = 'product__tiered-price-table-pricing-num';
    priceNum.textContent = priceText || '';

    const unit = document.createElement('span');
    unit.className = 'product__tiered-price-table-pricing-unit';
    unit.textContent = unitText || '';

    main.appendChild(priceNum);
    main.appendChild(unit);

    const savings = document.createElement('div');
    savings.className = 'product__tiered-price-table-pricing-savings';
    savings.textContent = savingsText || '';

    pricing.appendChild(main);
    pricing.appendChild(savings);
    tdPricing.appendChild(pricing);

    const tdCustomization = document.createElement('td');
    tdCustomization.className = 'product__tiered-price-table-customization';
    tdCustomization.textContent = customizationText || '';

    tr.appendChild(tdQty);
    tr.appendChild(tdPricing);
    tr.appendChild(tdCustomization);

    return tr;
  }

  render(target, rows, variantPriceCents, quantityPerCarton) {
    const table = target.querySelector('table');
    if (!table) return;

    let tbody = table.querySelector('tbody');
    if (!tbody) {
      tbody = document.createElement('tbody');
      table.appendChild(tbody);
    }

    tbody.innerHTML = '';

    rows.forEach((row) => {
      const discountPercent = Number(row.discountPercent || 0);
      const discountedCents = getDiscountedPriceCents(variantPriceCents, discountPercent);

      let unitText = '';
      if (quantityPerCarton) {
        const eachCents = getUnitPriceCents(discountedCents, quantityPerCarton);
        if (eachCents != null) {
          unitText = `${this.formatMoney(eachCents)}/pc`;
        }
      }

      let savingsText = '';
      if (discountPercent > 0) {
        const percentText = Number.isInteger(discountPercent) ? discountPercent : Number(discountPercent.toFixed(2));
        savingsText = `-${percentText}%off`;
      }

      tbody.appendChild(
        this.buildRow({
          qtyText: row.qtyText,
          priceText: this.formatMoney(discountedCents),
          unitText,
          savingsText,
          customizationText: row.customizationText
        })
      );
    });
  }

  queueSync() {
    if (this.syncQueued) return;
    this.syncQueued = true;

    requestAnimationFrame(() => {
      this.syncQueued = false;
      this.sync();
    });
  }

  sync() {
    const targets = this.getTargets();
    if (targets.length === 0) return;

    const variant = this.getCurrentVariant();
    if (!variant || typeof variant.price !== 'number') {
      targets.forEach((el) => {
        el.classList.add('hidden');
        el.dataset.facOpen = 'false';
        this.clearWrapperAnimationStyles(el);
      });
      targets.forEach((el) => delete el.dataset.facReady);
      targets.forEach((el) => delete el.dataset.facKey);
      this.lastDataKey = null;
      return;
    }

    const meta = this.getVariantMeta(variant.id);
    const rows = this.parseFactoryDiscountData(meta?.metafields?.factory_discount_data);
    if (!rows) {
      targets.forEach((el) => {
        el.classList.add('hidden');
        el.dataset.facOpen = 'false';
        this.clearWrapperAnimationStyles(el);
      });
      targets.forEach((el) => delete el.dataset.facReady);
      targets.forEach((el) => delete el.dataset.facKey);
      this.lastDataKey = null;
      return;
    }

    targets.forEach((el) => {
      el.dataset.facReady = 'true';
    });

    const quantityPerCarton = this.getQuantityPerCarton(variant);
    const nextKey = JSON.stringify({
      variantId: variant.id,
      basePrice: variant.price,
      quantityPerCarton,
      rows
    });

    const targetsNeedingRender = targets.filter((el) => el.dataset.facKey !== nextKey);
    if (targetsNeedingRender.length > 0) {
      targetsNeedingRender.forEach((el) => this.render(el, rows, variant.price, quantityPerCarton));
      targetsNeedingRender.forEach((el) => {
        el.dataset.facKey = nextKey;
      });
    }

    // Keep default hidden when data changes; "Inquire now" toggles visibility
    if (nextKey !== this.lastDataKey) {
      targets.forEach((el) => {
        el.classList.add('hidden');
        el.dataset.facOpen = 'false';
        this.clearWrapperAnimationStyles(el);
      });
      this.lastDataKey = nextKey;
    }
  }
}

// Initialize on load
document.addEventListener('DOMContentLoaded', () => {
  // Delay slightly to ensure theme.js has initialized and set initial state
  setTimeout(() => {
    new QuantityDiscountManager();
    new TieredPriceTableWebManager();
    new TieredPriceTableFacManager();
  }, 100);
});
